/**
 * CaseSheetPage
 *
 * Displays a comprehensive consultation summary/case sheet using the CaseSheetView component.
 * Handles data fetching, state management, and business logic while delegating UI rendering to the view.
 */

import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import {
  parseAyurvedaFromConclusion,
  buildAyurvedaDataFromSoapData,
} from '@/ayurveda/utils/parseAyurveda';
import type { PersonalHistory } from '@/ayurveda/types/ayurveda';
import {
  CaseSheetView,
  type SimplifiedMedication,
  type CaseSheetEditableFields,
  type DiagnosisCodeItem,
} from '@/components/views/CaseSheetView';
import { toStructuredMedication } from '@/lib/utils/medications';
import { buildFhirSummaryPayload } from '@/lib/utils/caseSheetFhirPayload';
import {
  clearCaseSheetBackup,
  readCaseSheetBackup,
  writeCaseSheetBackup,
} from '@/lib/utils/caseSheetBackup';
import { getErrorMessage } from '@/lib/api/client';
import {
  formatDiagnosisEntry,
  formatInvestigationEntry,
  formatRelationConditionEntry,
  formatRelationConditionList,
  isInvestigationItem,
  isRelationConditionItem,
} from '@/lib/utils/clinicalText';
import { medplumResourcesApi } from '@/lib/api/medplum-resources';
import { vitalsApi } from '@/lib/api/vitals';
import appointmentsApi from '@/lib/api/appointments';
import { useIsAyurveda } from '@/ayurveda';
import { patientsApi } from '@/lib/api/patients';
import { getPatientNameFromFhir } from '@/lib/types/patient';
import { toast } from 'sonner';
import type {
  AIAnalysisResult,
  AIAnalysisField,
  CDSSSuggestion,
  SessionSummary,
  PranikCDSS,
  ReferenceSlokaItem,
  VitalSigns,
  ClinicalAssessmentEntry,
  PrescribedMedicine,
} from '@/lib/types/consultation';
import { createComponentLogger } from '@/lib/utils/logger';
import { coerceToText } from '@/lib/utils/clinicalText';
import {
  buildCaseSheetSummaryText,
  type CaseSheetSummaryInput,
} from '@/lib/utils/caseSheetSummary';
import { generateCaseSheetPDF } from '@/lib/utils/pdfGenerator';
import { useConsultationRecovery } from '@/lib/hooks/useConsultationRecovery';
import { useVoiceNote } from '@/lib/hooks/useVoiceNote';
import type { VoiceSmartUpdateResponse } from '@/lib/api/voice-note';
import { useAuthStore } from '@/lib/stores/authStore';
import { draftsApi } from '@/lib/api/drafts';
import { caseSheetSummaryApi, type CaseSheetSummaryRecord } from '@/lib/api/case-sheet-summary';
import { investigationOrdersApi } from '@/lib/api/investigation-orders';

const logger = createComponentLogger('CaseSheetPage');

const createEmptyEditableFields = (): CaseSheetEditableFields => ({
  patientSummary: '',
  chiefComplaint: '',
  clinicalSymptoms: '',
  medicalHistory: '',
  personalHistory: undefined,
  familyHistory: '',
  generalExamination: '',
  investigations: '',
  prescribedMedications: [],
  treatmentPlan: '',
  doctorNotes: '',
  doshaProfile: '',
  allergies: '',
  socialHistory: '',
  reviewOfSystems: '',
  followUpPlan: '',
  assessmentDiagnosis: '',
  pediatricMilestone: '',
  adtOrder: '',
  medicationHistory: '',
  dietAdvice: '',
  diagnosisCodes: [],
  diagnosisAyurvedic: '',
  diagnosisAllopathic: '',
  treatmentMedicines: '',
  treatmentMedicinesTable: [],
  treatmentPrinciple: '',
  treatmentGiven: '',
  dietNotes: '',
  lifestyleNotes: '',
  viruddhaAhaaraNotes: '',
});

const cloneMedications = (medications: SimplifiedMedication[]): SimplifiedMedication[] =>
  medications.map((med) => ({ ...med, timing: [...med.timing] }));

const cloneDiagnosisCodes = (items: DiagnosisCodeItem[] = []): DiagnosisCodeItem[] =>
  items.map((item) => ({ ...item }));

const cloneTreatmentMedicinesTable = (items: PrescribedMedicine[] = []): PrescribedMedicine[] =>
  items.map((item) => ({ ...item }));

type TextFieldKey = Exclude<
  keyof CaseSheetEditableFields,
  'prescribedMedications' | 'diagnosisCodes' | 'treatmentMedicinesTable' | 'personalHistory'
>;

const cloneEditableFields = (fields: CaseSheetEditableFields): CaseSheetEditableFields => ({
  patientSummary: fields.patientSummary,
  chiefComplaint: fields.chiefComplaint,
  clinicalSymptoms: fields.clinicalSymptoms,
  medicalHistory: fields.medicalHistory,
  personalHistory: fields.personalHistory ? { ...fields.personalHistory } : undefined,
  familyHistory: fields.familyHistory,
  generalExamination: fields.generalExamination,
  investigations: fields.investigations,
  prescribedMedications: cloneMedications(fields.prescribedMedications),
  treatmentPlan: fields.treatmentPlan,
  doctorNotes: fields.doctorNotes,
  allergies: fields.allergies || '',
  socialHistory: fields.socialHistory || '',
  reviewOfSystems: fields.reviewOfSystems || '',
  followUpPlan: fields.followUpPlan || '',
  assessmentDiagnosis: fields.assessmentDiagnosis || '',
  pediatricMilestone: fields.pediatricMilestone || '',
  adtOrder: fields.adtOrder || '',
  medicationHistory: fields.medicationHistory || '',
  dietAdvice: fields.dietAdvice || '',
  doshaProfile: fields.doshaProfile ?? '',
  diagnosisCodes: cloneDiagnosisCodes(fields.diagnosisCodes),
  diagnosisAyurvedic: fields.diagnosisAyurvedic ?? '',
  diagnosisAllopathic: fields.diagnosisAllopathic ?? '',
  treatmentMedicines: fields.treatmentMedicines ?? '',
  treatmentMedicinesTable: cloneTreatmentMedicinesTable(fields.treatmentMedicinesTable),
  treatmentPrinciple: fields.treatmentPrinciple ?? '',
  treatmentGiven: fields.treatmentGiven ?? '',
  dietNotes: fields.dietNotes ?? '',
  lifestyleNotes: fields.lifestyleNotes ?? '',
  viruddhaAhaaraNotes: fields.viruddhaAhaaraNotes ?? '',
});

export const buildVoiceSummaryPayload = (
  fields: CaseSheetEditableFields,
  sessionSummary?: SessionSummary
): Record<string, unknown> => ({
  chief_complaint: { value: fields.chiefComplaint },
  history_of_present_illness: { value: fields.clinicalSymptoms },
  past_medical_surgical_history: { value: fields.medicalHistory },
  family_history: { value: fields.familyHistory },
  physical_examination: { value: fields.generalExamination },
  treatment_plan_management: { value: fields.treatmentPlan },
  doctor_notes: { value: fields.doctorNotes },
  follow_up_plan: { value: fields.followUpPlan },
  patient_summary: { value: fields.patientSummary },
  medications: { value: fields.prescribedMedications },
  assessment_and_diagnosis: sessionSummary?.assessment_and_diagnosis ?? { value: '' },
  investigations_orders: sessionSummary?.investigations_orders ?? { value: [] },
});

// Editable fields the temp-summary DB row has no column for. While any holds content the
// browser backup is the only copy, so it must not be cleared after a DB autosave.
const hasFieldsOutsideDb = (fields: CaseSheetEditableFields): boolean =>
  (fields.doshaProfile ?? '').trim().length > 0 ||
  (fields.diagnosisAyurvedic ?? '').trim().length > 0 ||
  (fields.treatmentPrinciple ?? '').trim().length > 0 ||
  Object.values(fields.personalHistory ?? {}).some((v) => (v ?? '').trim().length > 0);

const fieldsHaveContent = (fields: CaseSheetEditableFields): boolean =>
  fields.patientSummary.trim().length > 0 ||
  fields.chiefComplaint.trim().length > 0 ||
  fields.clinicalSymptoms.trim().length > 0 ||
  fields.medicalHistory.trim().length > 0 ||
  fields.familyHistory.trim().length > 0 ||
  fields.generalExamination.trim().length > 0 ||
  fields.investigations.trim().length > 0 ||
  fields.prescribedMedications.length > 0 ||
  fields.treatmentPlan.trim().length > 0 ||
  fields.doctorNotes.trim().length > 0 ||
  (fields.doshaProfile ?? '').trim().length > 0 ||
  (fields.allergies || '').trim().length > 0 ||
  (fields.socialHistory || '').trim().length > 0 ||
  (fields.reviewOfSystems || '').trim().length > 0 ||
  (fields.followUpPlan || '').trim().length > 0 ||
  (fields.assessmentDiagnosis || '').trim().length > 0 ||
  (fields.pediatricMilestone || '').trim().length > 0 ||
  (fields.adtOrder || '').trim().length > 0 ||
  (fields.medicationHistory || '').trim().length > 0 ||
  (fields.dietAdvice || '').trim().length > 0 ||
  (fields.diagnosisCodes || []).length > 0 ||
  (fields.diagnosisAyurvedic || '').trim().length > 0 ||
  (fields.diagnosisAllopathic || '').trim().length > 0 ||
  (fields.treatmentMedicines || '').trim().length > 0 ||
  (fields.treatmentPrinciple || '').trim().length > 0 ||
  (fields.treatmentGiven || '').trim().length > 0 ||
  Object.values(fields.personalHistory ?? {}).some((v) => (v ?? '').trim().length > 0) ||
  (fields.dietNotes || '').trim().length > 0 ||
  (fields.lifestyleNotes || '').trim().length > 0 ||
  (fields.viruddhaAhaaraNotes || '').trim().length > 0;

// Display labels for the Astha Sthaana Pareeksha + Prakruthi/Dosha/Aama
// clinical_assessment parameters, matching AyurvedicAssessmentCard.tsx's
// ASTHA_STHAANA_GROUPS exactly, so the Case Sheet shows the same terms the
// doctor selected during the live consultation.
const ASTHA_STHAANA_PAREEKSHA_LABELS: Array<[string, string]> = [
  ['nadi', 'Nadi'],
  ['mootram', 'Mootram'],
  ['malam', 'Malam'],
  ['jihva', 'Jihva'],
  ['sabdam', 'Sabdam'],
  ['sparsham', 'Sparsham'],
  ['drik', 'Drik'],
  ['akrithi', 'Akrithi'],
  ['prakruthi', 'Prakruthi'],
  ['dosha', 'Dosha'],
  ['aama', 'Aama'],
];

/**
 * Builds `<li><strong>Label</strong>: Value</li>` bullet items (no wrapping
 * `<ul>` — the caller combines these with any other bullets into one shared
 * list) for whichever Astha Sthaana Pareeksha / Prakruthi / Dosha / Aama
 * parameters have a value, from a `{ parameter: value }` map. Returns []
 * if none have a value.
 */
function buildAsthaSthaanaPareekshaItems(
  values: Record<string, string | null | undefined>
): string[] {
  return ASTHA_STHAANA_PAREEKSHA_LABELS.filter(([key]) => values[key]).map(
    ([key, label]) => `<li><strong>${label}</strong>: ${values[key]}</li>`
  );
}

/**
 * The backend sometimes sends dosha_profile as an already-formatted plain
 * string like "Nadi: Vata; Mutram: Vikrita; Drik: Samanya" instead of a
 * structured object — with its own label spelling, not necessarily matching
 * ASTHA_STHAANA_PAREEKSHA_LABELS exactly. Reformat any "Label: Value"
 * semicolon-separated string into bullet `<li>` items so it displays the
 * same way regardless of wording. Returns null if the string doesn't look
 * like this pattern (falls back to showing the plain text as-is).
 */
function tryConvertLabelledPairsToBulletHtml(text: string): string | null {
  const segments = text
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean);
  if (segments.length === 0) return null;

  const items: string[] = [];
  for (const segment of segments) {
    const colonIndex = segment.indexOf(':');
    if (colonIndex <= 0) return null; // not a "Label: Value" shape — bail out
    const label = segment.slice(0, colonIndex).trim();
    const value = segment.slice(colonIndex + 1).trim();
    if (!label || !value) return null;
    items.push(`<li><strong>${label}</strong>: ${value}</li>`);
  }
  return `<ul>${items.join('')}</ul>`;
}

/**
 * Extracts a string array from various field formats produced by AI/session data.
 * Pure function — safe to call outside the React render cycle.
 */
function extractStringArray(field: unknown): string[] {
  if (!field) return [];
  if (Array.isArray(field)) {
    return field
      .map((item) => {
        // If item is a string, return it directly
        if (typeof item === 'string') return item;
        // If item is an object, try to extract text content
        if (typeof item === 'object' && item !== null) {
          const obj = item as Record<string, unknown>;
          // Try common field names for text content
          const val =
            obj.text ??
            obj.recommendation ??
            obj.advice ??
            obj.description ??
            obj.name ??
            obj.value ??
            '';
          return String(val);
        }
        return String(item);
      })
      .filter(Boolean);
  }
  if (typeof field === 'string' && field.trim()) {
    return field
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (typeof field === 'object' && field !== null && 'value' in field) {
    return extractStringArray((field as { value: unknown }).value);
  }
  return [];
}

function firstNonEmptyArray(...fields: unknown[]): string[] {
  for (const field of fields) {
    const items = extractStringArray(field);
    if (items.length > 0) return items;
  }
  return [];
}

/**
 * Extract individual clinical items from a free-text block that match any of the given patterns.
 * Used as fallback when structured arrays (diet_modifications etc.) are empty but the
 * Ayurveda summarizer put everything into treatment_plan text.
 */
function extractClinicalItems(text: unknown, patterns: RegExp[]): string[] {
  if (!text || typeof text !== 'string') return [];
  // Split on sentence boundaries and list delimiters
  const sentences = text
    .replace(/\.\s+/g, '.\n')
    .split(/\n|;\s*/)
    .map((s) => s.replace(/^[-•*\d.]+\s*/, '').trim())
    .filter(Boolean);
  return sentences.filter((s) => patterns.some((p) => p.test(s))).slice(0, 8);
}

/**
 * CaseSheetPage Component
 */
/**
 * Whether a saved case sheet row carries any real clinical content. Quick Submit saves a
 * placeholder row (only `pranikCdss.quickSubmit`) that must not shadow the Medplum copy.
 */
function caseSheetRecordHasContent(record?: CaseSheetSummaryRecord | null): boolean {
  if (!record) return false;
  const textFields = [
    record.patientSummary,
    record.chiefComplaint,
    record.clinicalSymptoms,
    record.medicalHistory,
    record.familyHistory,
    record.generalExamination,
    record.treatmentPlan,
    record.doctorNotes,
    record.assessmentDiagnosis,
    record.socialHistory,
    record.reviewOfSystems,
    record.allergies,
    record.followUpPlan,
    record.dietAdvice,
    record.medicationHistory,
    record.pediatricMilestone,
    record.adtOrder,
  ];
  if (textFields.some((value) => typeof value === 'string' && value.trim())) return true;
  if (Array.isArray(record.medications) && record.medications.length > 0) return true;
  if (Array.isArray(record.diagnosisCodes) && record.diagnosisCodes.length > 0) return true;
  // Investigations may be stored bare or wrapped as `{ value: [...] }`.
  const rawInvestigations = record.investigations;
  const investigations =
    rawInvestigations && typeof rawInvestigations === 'object' && 'value' in rawInvestigations
      ? (rawInvestigations as { value?: unknown }).value
      : rawInvestigations;
  return Array.isArray(investigations)
    ? investigations.length > 0
    : typeof investigations === 'string' && investigations.trim().length > 0;
}

export default function CaseSheetPage() {
  // URL pattern: /case-sheet/:consultationId (always uses Encounter ID)
  const { consultationId } = useParams<{ consultationId?: string }>();

  const navigate = useNavigate();
  const isAyurveda = useIsAyurveda();
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  const [isSaved, setIsSaved] = useState(false);
  const [editableFields, setEditableFields] = useState<CaseSheetEditableFields | null>(null);
  const [hasUserEditedFields, setHasUserEditedFields] = useState(false);
  const [backupChecked, setBackupChecked] = useState(false);
  const [autosave, setAutosave] = useState<{
    status: 'idle' | 'saving' | 'saved' | 'error';
    at?: number;
  }>({ status: 'idle' });
  const [voiceNoteError, setVoiceNoteError] = useState<string | null>(null);

  useEffect(() => {
    setEditableFields(null);
    setHasUserEditedFields(false);
    setBackupChecked(false);
    setAutosave({ status: 'idle' });
  }, [consultationId]);

  const coerceToStringArray = useCallback((value: unknown): string[] => {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      // Family-history-style entries render as one numbered, labeled block
      // instead of being comma-joined with everything else.
      if (value.length > 0 && value.every(isRelationConditionItem)) {
        return [formatRelationConditionList(value)];
      }
      return value
        .map((item) => {
          if (typeof item === 'string') {
            return item;
          }
          if (typeof item === 'object' && item !== null) {
            // Handle family history objects (mixed-array fallback — the
            // common all-relation-condition case is short-circuited above)
            if (isRelationConditionItem(item)) return formatRelationConditionEntry(item);
            // Handle investigation/lab-result objects, e.g. { test, value, unit, notes }
            if (isInvestigationItem(item)) return formatInvestigationEntry(item);
            // Handle diagnosis objects
            if ('diagnosis' in item)
              return formatDiagnosisEntry(item as { diagnosis: unknown; notes?: unknown });
            // Handle examination objects
            if ('type' in item && 'value' in item) {
              const { type, value } = item as { type: string; value: string };
              return `${type}: ${value}`;
            }
          }
          try {
            return JSON.stringify(item);
          } catch {
            return String(item);
          }
        })
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }

    if (typeof value === 'string') {
      return value
        .split(/[,;\n]+/)
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }

    return [];
  }, []);

  const getFieldValue = useCallback(
    (field?: AIAnalysisField | null): string | undefined => {
      if (!field) {
        return undefined;
      }
      const values = coerceToStringArray(field.value);
      if (values.length === 0) {
        return undefined;
      }
      return values.join(', ');
    },
    [coerceToStringArray]
  );

  // Extract medical history — combine direct fields
  const extractMedicalHistory = useCallback(
    (aiAnalysis: AIAnalysisResult): string => {
      const parts: string[] = [];

      const directMedicalHistory = getFieldValue(aiAnalysis.medical_history);
      if (directMedicalHistory) {
        parts.push(directMedicalHistory);
      }

      const coMorbidities = getFieldValue(aiAnalysis.co_morbidity);
      if (coMorbidities) {
        parts.push(coMorbidities);
      }

      return parts.join('. ');
    },
    [getFieldValue]
  );

  const normalizeMedications = useCallback(
    (input: unknown): SimplifiedMedication[] => {
      if (!input) {
        return [];
      }

      if (Array.isArray(input)) {
        return input
          .map((item) => toStructuredMedication(item))
          .filter(
            (med): med is SimplifiedMedication => med !== null && med.medication.trim().length > 0
          );
      }

      if (typeof input === 'object') {
        const valueProp = (input as { value?: unknown }).value;
        if (Array.isArray(valueProp)) {
          return valueProp
            .map((item) => toStructuredMedication(item))
            .filter((m): m is SimplifiedMedication => m !== null && m.medication.trim().length > 0);
        }
        const names = coerceToStringArray(valueProp);
        return names.map((name) => ({
          medication: name,
          dosage: '',
          frequency: '',
          timing: [] as string[],
          duration: '',
        }));
      }

      return [];
    },
    [coerceToStringArray]
  );

  // Parse JSON conclusion to extract editable fields
  const parseJsonConclusion = useCallback(
    (
      conclusion?: string | null
    ): Partial<CaseSheetEditableFields> & {
      medications?: SimplifiedMedication[];
    } => {
      if (!conclusion) {
        return {};
      }

      try {
        const data = JSON.parse(conclusion);

        // Helper to extract value from nested format { value, timestamp, explanation }
        const getValue = (field: unknown): string => {
          if (!field) return '';
          if (typeof field === 'string') return field;
          if (Array.isArray(field)) {
            if (field.length > 0 && field.every(isRelationConditionItem)) {
              return formatRelationConditionList(field);
            }
            return field
              .map((item) => {
                if (typeof item === 'string') return item;
                if (typeof item === 'object' && item !== null) {
                  // Handle family history objects (mixed-array fallback)
                  if (isRelationConditionItem(item)) return formatRelationConditionEntry(item);
                  // Handle investigation/lab-result objects, e.g. { test, value, unit, notes }
                  if (isInvestigationItem(item)) return formatInvestigationEntry(item);
                  // Handle diagnosis objects
                  if ('diagnosis' in item)
                    return formatDiagnosisEntry(item as { diagnosis: unknown; notes?: unknown });
                  // Handle examination objects
                  if ('type' in item && 'value' in item) {
                    const { type, value } = item as { type: string; value: string };
                    return `${type}: ${value}`;
                  }
                }
                return String(item);
              })
              .join(', ');
          }
          if (typeof field === 'object' && field !== null && 'value' in field) {
            const val = (field as { value?: unknown }).value;
            if (!val) return '';
            if (typeof val === 'string') return val;
            if (Array.isArray(val)) {
              if (val.length > 0 && val.every(isRelationConditionItem)) {
                return formatRelationConditionList(val);
              }
              return (val as unknown[])
                .map((item) => {
                  if (typeof item === 'string') return item;
                  if (typeof item === 'object' && item !== null) {
                    // Handle family history objects (mixed-array fallback)
                    if (isRelationConditionItem(item)) return formatRelationConditionEntry(item);
                    // Handle investigation/lab-result objects, e.g. { test, value, unit, notes }
                    if (isInvestigationItem(item)) return formatInvestigationEntry(item);
                    // Handle diagnosis objects
                    if ('diagnosis' in item)
                      return formatDiagnosisEntry(item as { diagnosis: unknown; notes?: unknown });
                    // Handle examination objects
                    if ('type' in item && 'value' in item) {
                      const { type, value } = item as { type: string; value: string };
                      return `${type}: ${value}`;
                    }
                  }
                  return String(item);
                })
                .join(', ');
            }
          }
          return '';
        };

        const getArray = (field: unknown): string[] =>
          coerceToStringArray(
            typeof field === 'object' && field !== null && 'value' in field
              ? (field as { value?: unknown }).value
              : field
          );

        const firstText = (...fields: unknown[]): string => {
          for (const field of fields) {
            const value = getValue(field);
            if (value.trim()) return value;
          }
          return '';
        };

        const joinText = (...fields: unknown[]): string =>
          fields
            .flatMap((field) => getArray(field))
            .map((item) => item.trim())
            .filter(Boolean)
            .join('. ');

        // Extract medications as StructuredMedication objects
        const getMedications = (medsField: unknown): SimplifiedMedication[] => {
          if (!medsField) return [];

          let medsValue: unknown = medsField;
          if (typeof medsField === 'object' && medsField !== null && 'value' in medsField) {
            medsValue = (medsField as { value: unknown }).value;
          }

          if (Array.isArray(medsValue)) {
            return medsValue
              .map((med: unknown) => toStructuredMedication(med))
              .filter(
                (m): m is SimplifiedMedication => m !== null && m.medication.trim().length > 0
              );
          }

          return [];
        };

        const getMedicationLikeTreatmentItems = (items: unknown): SimplifiedMedication[] =>
          getArray(items)
            .filter((item) =>
              /\b(churna|rasa|pishti|taila|ghee|tablet|capsule|kwatha|arishta|asava|medicine|medication)\b/i.test(
                item
              )
            )
            .map((item) => toStructuredMedication(item))
            .filter(
              (med): med is SimplifiedMedication => med !== null && med.medication.trim().length > 0
            );

        return {
          patientSummary: firstText(data.patient_summary, data.assessment),
          chiefComplaint: firstText(data.chief_complaint),
          clinicalSymptoms: firstText(data.history_of_present_illness, data.chief_complaint),
          medicalHistory: joinText(
            data.past_medical_surgical_history,
            data.medication_history,
            data.known_diseases_history,
            data.known_diseases
          ),
          familyHistory: firstText(data.family_history),
          generalExamination: getValue(data.physical_examination),
          treatmentPlan: firstText(data.treatment_plan_management, data.treatment_plan),
          doctorNotes: getValue(data.follow_up_plan),
          allergies: firstText(data.allergies),
          socialHistory: firstText(data.social_history),
          reviewOfSystems: firstText(data.review_of_systems),
          followUpPlan: firstText(data.follow_up_plan),
          assessmentDiagnosis: firstText(data.assessment_and_diagnosis),
          pediatricMilestone: firstText(
            data.pediatric_milestone,
            data.pediatric_milestones,
            data.pediatricMilestone
          ),
          adtOrder: firstText(data.adt_order, data.adt_orders, data.adtOrder),
          medicationHistory: firstText(data.medication_history),
          dietAdvice: firstText(data.diet_advice, data.meal_plan, data.dietAdvice),
          medications: [
            ...getMedications(data.medications),
            ...getMedications(data.prescribed_medications),
            ...getMedications(data.suggested_medications),
            ...getMedicationLikeTreatmentItems(data.treatment_plan),
          ],
        };
      } catch {
        // Not valid JSON - return empty
        logger.warn('Failed to parse conclusion as JSON');
        return {};
      }
    },
    []
  );

  // Load encounter from Medplum
  const {
    data: encounter,
    isLoading: isLoadingEncounter,
    isError: isErrorEncounter,
  } = useQuery({
    queryKey: ['encounters', consultationId],
    queryFn: () => medplumResourcesApi.getEncounter(consultationId!),
    enabled: !!consultationId,
  });

  // Effective encounter and patientId derived from the loaded encounter
  const effectiveEncounterId = consultationId || encounter?.id;
  const patientId = encounter?.subject?.reference?.split('/')[1];

  // Fetch vitals for the encounter (read/edit in the Vitals accordion)
  const { data: encounterVitals } = useQuery({
    queryKey: ['vitals', effectiveEncounterId],
    queryFn: () => vitalsApi.getVitalsForEncounter(effectiveEncounterId!),
    enabled: !!effectiveEncounterId,
    staleTime: 60000,
  });

  const saveVitalsMutation = useMutation({
    mutationFn: (vitals: VitalSigns) => {
      if (!patientId || !effectiveEncounterId) {
        throw new Error('Missing patientId or encounterId for saving vitals');
      }
      return vitalsApi.updateVitals({
        patientId,
        encounterId: effectiveEncounterId,
        vitals,
        practitionerId: user?.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vitals', effectiveEncounterId] });
      toast.success('Vitals saved successfully');
    },
    onError: (error) => {
      logger.error('Failed to save vitals:', error);
      toast.error('Failed to save vitals');
    },
  });

  const {
    data: patient,
    isLoading: isLoadingPatient,
    isError: isErrorPatient,
  } = useQuery({
    queryKey: ['patients', patientId],
    queryFn: () => medplumResourcesApi.getPatient(patientId!),
    enabled: !!patientId,
  });

  // Get aiAnalysis from location state (passed from ConsultationPage)
  type LocationStateType = {
    aiAnalysis?: AIAnalysisResult;
    sessionSummary?: SessionSummary;
    cdssSuggestions?: CDSSSuggestion[];
    // Astha Sthaana Pareeksha/Prakruthi/Dosha/Aama chip values, passed
    // straight from ConsultationPage's effectiveClinicalAssessment (live +
    // kiosk-snapshot fallback already merged) — the same data already shown
    // correctly on the consultation page's Ayurvedic Assessment card.
    clinicalAssessment?: ClinicalAssessmentEntry[];
    // Structured medicines table exactly as shown on the consultation page's
    // Treatment card — same reasoning as clinicalAssessment above.
    prescribedMedicines?: PrescribedMedicine[];
    // Duration/Mode of Onset exactly as shown on the consultation page's
    // Patient Information card — same reasoning as clinicalAssessment above.
    duration?: string | null;
    modeOfOnset?: string | null;
    // History/Personal History exactly as shown on the consultation page's
    // Patient Information card — same reasoning as clinicalAssessment above.
    pastIllness?: string | null;
    treatmentHistory?: string | null;
    historyFamilyHistory?: string | null;
    currentMedication?: string | null;
    familySocialOccupational?: string | null;
    personalHistory?: PersonalHistory | null;
    // Live `commonly_eaten_food` list, mapped into Personal History → Diet.
    commonlyEatenFood?: string[];
  } | null;

  const locationState = location.state as LocationStateType;

  // Recover persisted data from IndexedDB (for page refresh scenarios)
  const { summary: recoveredSummary } = useConsultationRecovery(consultationId || '', {
    autoRecover: true,
  });

  // Store location state once on mount to prevent loss during popstate events
  const preservedState = useRef<LocationStateType>(null);
  useEffect(() => {
    if (locationState && !preservedState.current) {
      preservedState.current = locationState;
    }
  }, [locationState]);

  const state = preservedState.current || locationState;
  const aiAnalysis = state?.aiAnalysis;
  const liveSessionSummary =
    state?.sessionSummary || (recoveredSummary as SessionSummary | undefined);
  const passedClinicalAssessment = state?.clinicalAssessment;
  const passedPrescribedMedicines = state?.prescribedMedicines;
  const passedDuration = state?.duration;
  const passedModeOfOnset = state?.modeOfOnset;
  const passedPastIllness = state?.pastIllness;
  const passedTreatmentHistory = state?.treatmentHistory;
  const passedHistoryFamilyHistory = state?.historyFamilyHistory;
  const passedCurrentMedication = state?.currentMedication;
  const passedFamilySocialOccupational = state?.familySocialOccupational;
  const passedPersonalHistory = state?.personalHistory;
  const passedCommonlyEatenFood = state?.commonlyEatenFood;

  const cdssSuggestions = useMemo(() => state?.cdssSuggestions || [], [state?.cdssSuggestions]);

  const aiMedications = normalizeMedications(aiAnalysis?.medications);

  // Fetch case sheet summary from DB FIRST (primary persistence source)
  const { data: savedDbSummary, isLoading: isLoadingDbSummary } = useQuery({
    queryKey: ['caseSheetSummary', consultationId],
    queryFn: () => caseSheetSummaryApi.getSummary(consultationId!),
    enabled: !!consultationId,
  });

  // Quick Submit leaves a placeholder row behind (it only carries the `quickSubmit`
  // marker, no case sheet fields). Treat a row with no real content as "no DB data"
  // so the Medplum fallbacks below can run instead of being blocked by an empty row.
  const dbSummary = useMemo(
    () => (caseSheetRecordHasContent(savedDbSummary) ? savedDbSummary : undefined),
    [savedDbSummary]
  );

  // Only fetch the Medplum copies as fallback when DB has no data
  // This prevents stale Medplum data from overriding fresh DB data
  const { data: encounterDiagnosticReport, isLoading: isLoadingEncounterDiagnosticReport } =
    useQuery({
      queryKey: ['encounterDiagnosticReport', consultationId],
      queryFn: () => medplumResourcesApi.getLatestDiagnosticReportForEncounter(consultationId!),
      enabled: !!consultationId && !isLoadingDbSummary && !dbSummary,
    });

  // The summary saved to Medplum through saveSummaryToFhir (e.g. by the agent on Quick
  // Submit) lives in the encounter's DocumentReference, not in a DiagnosticReport. It has
  // the same shape as a DiagnosticReport conclusion, so it feeds the same parsing below.
  const { data: fhirSummary, isLoading: isLoadingFhirSummary } = useQuery({
    queryKey: ['encounterFhirSummary', consultationId],
    queryFn: () => caseSheetSummaryApi.getFhirSummary(consultationId!),
    enabled: !!consultationId && !isLoadingDbSummary && !dbSummary,
    retry: 1,
  });

  // Fetch linked appointment to derive isSaved / completed status
  const { data: linkedAppointment } = useQuery({
    queryKey: ['appointmentForEncounter', consultationId],
    queryFn: () => appointmentsApi.getAppointmentForEncounter(consultationId!),
    enabled: !!consultationId,
    staleTime: 30000,
  });

  // With no live/recovered summary (e.g. Verify opened from the dashboard after a Quick
  // Submit), the summary saved to Medplum stands in for it. It is the same payload the
  // AI produced, so rendering (investigations, symptom codes, CDSS alerts) and the FHIR
  // re-save keep their structured data and provenance instead of seeing an empty source.
  const sessionSummary =
    liveSessionSummary ?? (fhirSummary ? (fhirSummary as unknown as SessionSummary) : undefined);

  const latestReportConclusion = fhirSummary
    ? JSON.stringify(fhirSummary)
    : encounterDiagnosticReport?.conclusion;

  // Independent fallback source for Duration/Mode of Onset (and any other
  // kiosk-derived field) when this Case Sheet is opened WITHOUT fresh
  // navigation state from ConsultationPage (e.g. reopened later, page
  // refresh) — fetches the same kiosk Composition ConsultationPage.tsx reads,
  // so this page doesn't depend on the navigation-state pass-through at all.
  const { data: kioskSoapData } = useQuery({
    queryKey: ['preConsultSOAP', consultationId],
    queryFn: () => medplumResourcesApi.getLatestCompositionForEncounter(consultationId!),
    enabled: !!consultationId && isAyurveda,
    staleTime: 60000,
  });
  const kioskAyurvedaData = useMemo(
    () => (kioskSoapData ? buildAyurvedaDataFromSoapData(kioskSoapData) : null),
    [kioskSoapData]
  );
  // Extracts a plain-string session-summary field, handling both the raw
  // string and `{value, timestamp, explanation}`-wrapped formats — a small
  // local stand-in for `getSessionFieldValue` (defined further down this
  // component, after these consts, so not usable here).
  const sessionFieldToString = (field: unknown): string | undefined => {
    if (!field) return undefined;
    if (typeof field === 'string') return field.trim() || undefined;
    if (typeof field === 'object' && field !== null && 'value' in field) {
      const v = (field as { value?: unknown }).value;
      return typeof v === 'string' ? v.trim() || undefined : undefined;
    }
    return undefined;
  };

  // Duration/Mode of Onset — same values shown on the consultation page's
  // Patient Information card. Priority: fresh navigation right after
  // Submit, then the post-consultation session summary (the backend's
  // `chief_complaint_duration`/`chief_complaint_mode_of_onset` fields —
  // reliable even when the live chip never fired), then the
  // independently-fetched kiosk Composition when reopened later with no
  // nav state or session summary.
  const duration =
    passedDuration ??
    sessionFieldToString(sessionSummary?.chief_complaint_duration) ??
    kioskAyurvedaData?.duration ??
    undefined;
  const modeOfOnset =
    passedModeOfOnset ??
    sessionFieldToString(sessionSummary?.chief_complaint_mode_of_onset) ??
    kioskAyurvedaData?.modeOfOnset ??
    undefined;
  // History/Personal History — same fallback reasoning as duration/modeOfOnset above.
  const pastIllness =
    passedPastIllness ??
    sessionFieldToString(sessionSummary?.past_illness) ??
    kioskAyurvedaData?.pastIllness ??
    undefined;
  const treatmentHistory =
    passedTreatmentHistory ??
    sessionFieldToString(sessionSummary?.treatment_history) ??
    kioskAyurvedaData?.treatmentHistory ??
    undefined;
  const historyFamilyHistory =
    passedHistoryFamilyHistory ?? kioskAyurvedaData?.historyFamilyHistory ?? undefined;
  const currentMedication =
    passedCurrentMedication ?? kioskAyurvedaData?.currentMedication ?? undefined;
  const familySocialOccupational =
    passedFamilySocialOccupational ??
    sessionFieldToString(sessionSummary?.family_social_occupational) ??
    kioskAyurvedaData?.familySocialOccupational ??
    undefined;
  // Personal History (Diet/Appetite/Sleep/Exercise/Bowel/Bladder/Addiction/
  // Menstrual) — same priority order and reasoning as diagnosis below: fresh
  // navigation right after Submit, then the post-consultation session
  // summary's `personal_history` object (reliable even when the live delta
  // only ever reported `addiction`), then the saved DiagnosticReport
  // conclusion's `personal_history` JSON object (so a reopened Case Sheet
  // recovers it), then the kiosk Composition.
  const sessionPersonalHistory: PersonalHistory | undefined =
    sessionSummary?.personal_history && typeof sessionSummary.personal_history === 'object'
      ? (sessionSummary.personal_history as PersonalHistory)
      : undefined;
  let savedPersonalHistory: PersonalHistory | undefined;
  if (latestReportConclusion) {
    try {
      const reportJson = JSON.parse(latestReportConclusion) as Record<string, unknown>;
      const ph = reportJson?.personal_history;
      if (ph && typeof ph === 'object') {
        savedPersonalHistory = ph as PersonalHistory;
      }
    } catch {
      // malformed JSON — ignore
    }
  }
  const rawPersonalHistory =
    passedPersonalHistory ??
    sessionPersonalHistory ??
    savedPersonalHistory ??
    kioskAyurvedaData?.personalHistory ??
    undefined;
  // `commonly_eaten_food` (CDSS-extracted list of foods the patient regularly
  // eats, e.g. ['South Indian food']) has no dedicated Personal History key
  // of its own from the backend — map it into Diet whenever Diet is still
  // empty, so it isn't lost.
  const commonlyEatenFoodText = (() => {
    // The live list handed over from the consultation page wins; the final
    // summary only carries it sometimes, so it is the fallback.
    if (passedCommonlyEatenFood && passedCommonlyEatenFood.length > 0) {
      return passedCommonlyEatenFood.filter((v) => typeof v === 'string').join(', ') || undefined;
    }
    const raw = sessionSummary?.commonly_eaten_food as unknown;
    const list = Array.isArray(raw)
      ? raw
      : typeof raw === 'object' && raw !== null && Array.isArray((raw as { value?: unknown }).value)
        ? (raw as { value: unknown[] }).value
        : undefined;
    return Array.isArray(list)
      ? list.filter((v): v is string => typeof v === 'string').join(', ') || undefined
      : undefined;
  })();
  const personalHistory: PersonalHistory | undefined =
    rawPersonalHistory || commonlyEatenFoodText
      ? {
          diet: rawPersonalHistory?.diet || commonlyEatenFoodText || null,
          appetite: rawPersonalHistory?.appetite ?? null,
          sleep: rawPersonalHistory?.sleep ?? null,
          exercise: rawPersonalHistory?.exercise ?? null,
          bowel: rawPersonalHistory?.bowel ?? null,
          bladder: rawPersonalHistory?.bladder ?? null,
          addiction: rawPersonalHistory?.addiction ?? null,
          menstrual: rawPersonalHistory?.menstrual ?? null,
        }
      : undefined;
  // Ayurvedic/Allopathic diagnosis — same clinical_assessment chip data
  // already shown on the consultation page's Diagnosis tiles (parameter keys
  // 'diagnosis_ayurvedic'/'diagnosis_allopathic'). Priority order:
  // 1. passedClinicalAssessment (fresh navigation right after the
  //    consultation) — the diagnosis is very often confirmed live during the
  //    consultation itself, not pre-filled by the kiosk, so this is the
  //    primary source, not just a nice-to-have.
  // 2. The post-consultation session summary's `diagnosis` object (backend
  //    keys: `ayurvedic_diagnosis`/`allopathic_diagnosis`) — reliable even
  //    when the live chip never fired.
  // 3. The saved DiagnosticReport conclusion's `diagnosis` JSON object —
  //    persisted on Save Record (see the save payload below) specifically so
  //    a reopened Case Sheet can recover a live-confirmed diagnosis that the
  //    kiosk never captured.
  // 4. The kiosk Composition's diagnosis section — only ever has data when
  //    the kiosk/PA itself recorded a provisional diagnosis.
  const sessionDiagnosis = sessionSummary?.diagnosis as
    | { ayurvedic_diagnosis?: string | null; allopathic_diagnosis?: string | null }
    | undefined;
  let savedDiagnosis: { ayurvedic?: string | null; allopathic?: string | null } | undefined;
  if (latestReportConclusion) {
    try {
      const reportJson = JSON.parse(latestReportConclusion) as Record<string, unknown>;
      const d = reportJson?.diagnosis;
      if (d && typeof d === 'object') {
        savedDiagnosis = d as { ayurvedic?: string | null; allopathic?: string | null };
      }
    } catch {
      // malformed JSON — ignore
    }
  }
  const diagnosisAyurvedic =
    passedClinicalAssessment?.find((e) => e.parameter === 'diagnosis_ayurvedic')?.value ??
    sessionFieldToString(sessionDiagnosis?.ayurvedic_diagnosis) ??
    savedDiagnosis?.ayurvedic ??
    kioskAyurvedaData?.diagnosis?.ayurvedic ??
    undefined;
  const diagnosisAllopathic =
    passedClinicalAssessment?.find((e) => e.parameter === 'diagnosis_allopathic')?.value ??
    sessionFieldToString(sessionDiagnosis?.allopathic_diagnosis) ??
    savedDiagnosis?.allopathic ??
    kioskAyurvedaData?.diagnosis?.allopathic ??
    undefined;

  // Treatment: Medicines / Treatment Principle / Treatment Given — same
  // priority order and reasoning as diagnosis above.
  let savedTreatment:
    | {
        medicines?: string | string[] | null;
        treatmentPrinciple?: string | null;
        treatmentGiven?: string | null;
        prescribedMedicines?: PrescribedMedicine[] | null;
      }
    | undefined;
  if (latestReportConclusion) {
    try {
      const reportJson = JSON.parse(latestReportConclusion) as Record<string, unknown>;
      const t = reportJson?.treatment;
      if (t && typeof t === 'object') {
        savedTreatment = t as typeof savedTreatment;
      }
    } catch {
      // malformed JSON — ignore
    }
  }
  const kioskMedicines = kioskAyurvedaData?.treatment?.medicines;
  const treatmentMedicines =
    passedClinicalAssessment?.find((e) => e.parameter === 'treatment_medicines')?.value ??
    (Array.isArray(savedTreatment?.medicines)
      ? savedTreatment.medicines.join(', ')
      : savedTreatment?.medicines) ??
    (Array.isArray(kioskMedicines) ? kioskMedicines.join(', ') : undefined);

  // Structured medicines table from the post-consultation session summary —
  // the AI's catalog-matched `prescribed_medicines` array (name/form/dose/
  // anupana/timing/frequency/duration/additional_info), same schema as the
  // live CDSS stream. This is the source that's actually populated today
  // (the live nav-state source below rarely fires yet, since the backend
  // mostly emits this only in the post-session summary, not mid-consult).
  const unwrapMedsField = (field: unknown): unknown[] | undefined => {
    if (!field) return undefined;
    if (Array.isArray(field)) return field;
    if (typeof field === 'object' && field !== null && 'value' in field) {
      const val = (field as { value: unknown }).value;
      return Array.isArray(val) ? val : undefined;
    }
    return undefined;
  };
  const sessionMedicinesRaw =
    unwrapMedsField(sessionSummary?.prescribed_medicines) ??
    unwrapMedsField(sessionSummary?.medications);
  const sessionPrescribedMedicines: PrescribedMedicine[] | undefined = sessionMedicinesRaw
    ?.map((med): PrescribedMedicine | null => {
      if (!med || typeof med !== 'object') return null;
      const obj = med as Record<string, unknown>;
      const name = typeof obj.name === 'string' ? obj.name : undefined;
      if (!name) return null;
      const asString = (v: unknown): string | null => (typeof v === 'string' ? v : null);
      return {
        name,
        form: asString(obj.form),
        dose: asString(obj.dose),
        anupana: asString(obj.anupana),
        timing: asString(obj.timing),
        frequency: asString(obj.frequency),
        duration: asString(obj.duration),
        additional_info: asString(obj.additional_info),
      };
    })
    .filter((m): m is PrescribedMedicine => m !== null);

  // Structured medicines table — no kiosk-snapshot fallback (the kiosk
  // Composition only ever has a plain string[] of medicine names).
  const treatmentMedicinesTable: PrescribedMedicine[] | undefined =
    passedPrescribedMedicines && passedPrescribedMedicines.length > 0
      ? passedPrescribedMedicines
      : sessionPrescribedMedicines && sessionPrescribedMedicines.length > 0
        ? sessionPrescribedMedicines
        : savedTreatment?.prescribedMedicines && savedTreatment.prescribedMedicines.length > 0
          ? savedTreatment.prescribedMedicines
          : undefined;
  // Backend sends two distinct fields for this: `treatment_principle_stated`
  // (the Vaidya's own literal quote, a plain string) and `treatment_principle`
  // (the AI-classified tags, e.g. ["Rasayana", "Deepana-Pachana"]). Prefer
  // the Vaidya's literal words when present, falling back to the
  // AI-classified list — distinct from the saved-report shape, which stores
  // this as a single joined string either way.
  const sessionTreatmentPrinciple =
    sessionFieldToString(sessionSummary?.treatment_principle_stated) ??
    (Array.isArray(sessionSummary?.treatment_principle)
      ? (sessionSummary.treatment_principle as unknown[])
          .filter((v): v is string => typeof v === 'string')
          .join(', ')
      : undefined);
  const treatmentPrinciple =
    passedClinicalAssessment?.find((e) => e.parameter === 'treatment_principle')?.value ??
    (sessionTreatmentPrinciple || undefined) ??
    savedTreatment?.treatmentPrinciple ??
    kioskAyurvedaData?.treatment?.treatmentPrinciple ??
    undefined;
  const treatmentGiven =
    passedClinicalAssessment?.find((e) => e.parameter === 'treatment_given')?.value ??
    savedTreatment?.treatmentGiven ??
    kioskAyurvedaData?.treatment?.treatmentGiven ??
    undefined;

  // Parse medications from saved DiagnosticReport (JSON format)
  const reportData = parseJsonConclusion(latestReportConclusion);
  const reportMedications = reportData.medications || [];

  // DB summary medications (already structured)
  const dbMedications: SimplifiedMedication[] = Array.isArray(dbSummary?.medications)
    ? (dbSummary!.medications as unknown[])
        .map((m) => toStructuredMedication(m))
        .filter((m): m is SimplifiedMedication => m !== null)
    : [];

  // DB-persisted medications are authoritative once they exist — they carry
  // manual edits (relationWithMeal/additionalInfo/itemCode) that the raw AI
  // extraction never had. Only fall back to aiMedications/reportMedications
  // before anything has been saved yet.
  const prescribedMedications =
    dbMedications.length > 0
      ? dbMedications
      : aiMedications.length > 0
        ? aiMedications
        : reportMedications;

  // Helper to extract string value from SessionSummary field (handles nested { value } format)
  const getSessionFieldValue = useCallback((field?: unknown): string => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (Array.isArray(field)) {
      if (field.length > 0 && field.every(isRelationConditionItem)) {
        return formatRelationConditionList(field);
      }
      return (
        field
          .map((item) => {
            if (typeof item === 'string') return item;
            if (typeof item === 'object' && item !== null) {
              // Handle family history objects (mixed-array fallback)
              if (isRelationConditionItem(item)) return formatRelationConditionEntry(item);
              // Handle investigation/lab-result objects, e.g. { test, value, unit, notes }
              if (isInvestigationItem(item)) return formatInvestigationEntry(item);
              // Handle diagnosis objects
              if ('diagnosis' in item)
                return formatDiagnosisEntry(item as { diagnosis: unknown; notes?: unknown });
              // Handle examination objects
              if ('type' in item && 'value' in item) {
                const { type, value } = item as { type: string; value: string };
                return `${type}: ${value}`;
              }
            }
            return String(item);
          })
          // '\n', not ', ' — each array element is already the backend's own
          // discrete bullet boundary; a comma join is ambiguous whenever an
          // item has its own internal comma (see extractSessionField in
          // case-sheet-summary.ts for the full rationale, same fix here).
          .join('\n')
      );
    }
    if (typeof field === 'object' && field !== null && 'value' in field) {
      const fieldObj = field as { value?: unknown };
      if (!fieldObj.value) return '';
      if (typeof fieldObj.value === 'string') return fieldObj.value;
      if (Array.isArray(fieldObj.value)) {
        if (fieldObj.value.length > 0 && fieldObj.value.every(isRelationConditionItem)) {
          return formatRelationConditionList(fieldObj.value);
        }
        return (
          (fieldObj.value as unknown[])
            .map((item) => {
              if (typeof item === 'string') return item;
              if (typeof item === 'object' && item !== null) {
                // Handle family history objects (mixed-array fallback)
                if (isRelationConditionItem(item)) return formatRelationConditionEntry(item);
                // Handle investigation/lab-result objects, e.g. { test, value, unit, notes }
                if (isInvestigationItem(item)) return formatInvestigationEntry(item);
                // Handle diagnosis objects
                if ('diagnosis' in item)
                  return formatDiagnosisEntry(item as { diagnosis: unknown; notes?: unknown });
                // Handle examination objects
                if ('type' in item && 'value' in item) {
                  const { type, value } = item as { type: string; value: string };
                  return `${type}: ${value}`;
                }
              }
              return String(item);
            })
            // See the bare-array branch above for why '\n' and not ', '.
            .join('\n')
        );
      }
    }
    return '';
  }, []);

  const initialEditableFields = useMemo<CaseSheetEditableFields>(() => {
    const base = createEmptyEditableFields();

    // If sessionSummary is available (from LiveKit agent), use it (highest priority)
    if (sessionSummary) {
      const ss = sessionSummary as unknown as Record<string, unknown>;
      base.patientSummary = getSessionFieldValue(sessionSummary.patient_summary) || '';
      base.chiefComplaint = getSessionFieldValue(sessionSummary.chief_complaint) || '';
      // history_of_present_illness is the generic alias; fall back to Ayurveda-native
      // `symptoms` if the alias is empty (backend maps symptoms → history_of_present_illness).
      base.clinicalSymptoms =
        getSessionFieldValue(sessionSummary.history_of_present_illness) ||
        getSessionFieldValue(sessionSummary.symptoms) ||
        '';
      // --- Improved medicalHistory fallback ---
      base.medicalHistory =
        [
          getSessionFieldValue(sessionSummary.past_medical_surgical_history),
          getSessionFieldValue(sessionSummary.medication_history),
          getSessionFieldValue(sessionSummary.known_diseases_history),
          getSessionFieldValue(sessionSummary.known_diseases),
          getSessionFieldValue(ss.medical_history),
          getSessionFieldValue(ss.co_morbidity),
        ]
          .filter(Boolean)
          .join('. ') || '';
      // --- Improved familyHistory fallback ---
      base.familyHistory = getSessionFieldValue(sessionSummary.family_history) || '';
      base.generalExamination = getSessionFieldValue(sessionSummary.physical_examination) || '';
      // investigations_orders is the generic alias; fall back to Ayurveda-native
      // `relevant_investigations` if the alias is empty.
      base.investigations =
        getSessionFieldValue(sessionSummary.investigations_orders) ||
        getSessionFieldValue(sessionSummary.relevant_investigations) ||
        '';
      // treatment_plan_management is the generic alias; fall back to Ayurveda-native
      // `treatment_plan` if the alias is empty.
      base.treatmentPlan =
        getSessionFieldValue(sessionSummary.treatment_plan_management) ||
        getSessionFieldValue(sessionSummary.treatment_plan) ||
        '';
      base.doctorNotes = getSessionFieldValue(sessionSummary.follow_up_plan) || '';
      base.allergies = getSessionFieldValue(sessionSummary.allergies) || '';
      base.socialHistory = getSessionFieldValue(sessionSummary.social_history) || '';
      base.reviewOfSystems = getSessionFieldValue(sessionSummary.review_of_systems) || '';
      base.followUpPlan = getSessionFieldValue(sessionSummary.follow_up_plan) || '';
      // assessment_and_diagnosis is the generic alias; fall back to Ayurveda-native
      // `assessment` if the alias is empty.
      base.assessmentDiagnosis =
        getSessionFieldValue(sessionSummary.assessment_and_diagnosis) ||
        getSessionFieldValue(sessionSummary.assessment) ||
        '';
      base.pediatricMilestone = getSessionFieldValue(
        ss.pediatric_milestone ?? ss.pediatric_milestones ?? ss.pediatricMilestone
      );
      base.adtOrder = getSessionFieldValue(ss.adt_order ?? ss.adt_orders ?? ss.adtOrder);
      base.medicationHistory = getSessionFieldValue(sessionSummary.medication_history) || '';
      base.dietAdvice = getSessionFieldValue(ss.diet_advice ?? ss.meal_plan ?? ss.dietAdvice) || '';

      const isMedsNonEmpty = (field: unknown): boolean => {
        if (!field) return false;
        if (Array.isArray(field)) return field.length > 0;
        if (typeof field === 'object' && field !== null && 'value' in field) {
          const val = (field as { value: unknown }).value;
          return Array.isArray(val) ? val.length > 0 : !!val;
        }
        return true;
      };

      // Extract medications from sessionSummary.
      // Handle both direct value and nested { value } format.
      // Fall back to Ayurveda-native `prescribed_medicines` if `medications` is empty
      // (backend maps prescribed_medicines → medications, but be defensive).
      const medicationsField = isMedsNonEmpty(sessionSummary.medications)
        ? sessionSummary.medications
        : sessionSummary.prescribed_medicines;
      let medsValue: unknown = null;

      if (typeof medicationsField === 'string' || Array.isArray(medicationsField)) {
        // Direct string or array format
        medsValue = medicationsField;
      } else if (
        typeof medicationsField === 'object' &&
        medicationsField !== null &&
        'value' in medicationsField
      ) {
        // Nested { value } format
        medsValue = (medicationsField as { value: unknown }).value;
      }

      if (medsValue) {
        if (Array.isArray(medsValue)) {
          // Convert medications to StructuredMedication objects
          base.prescribedMedications = medsValue
            .map((med: unknown) => toStructuredMedication(med))
            .filter((m): m is SimplifiedMedication => m !== null && m.medication.trim().length > 0);
        }
      }

      // --- Ayurveda-specific field mapping ---
      // Use sessionSummary as Record to access dynamic Ayurveda fields
      // Map lifestyle_recommendations to treatmentPlan if not already set
      if (sessionSummary.lifestyle_recommendations && !base.treatmentPlan) {
        const lifestyleVal = getSessionFieldValue(sessionSummary.lifestyle_recommendations);
        if (lifestyleVal) {
          base.treatmentPlan = lifestyleVal;
        }
      }

      // Map ayurvedic_medicines to prescribedMedications (if not already set)
      if (base.prescribedMedications.length === 0 && sessionSummary.ayurvedic_medicines) {
        const ayurField = sessionSummary.ayurvedic_medicines as { value?: unknown } | unknown;
        const ayurMeds =
          typeof ayurField === 'object' && ayurField !== null && 'value' in ayurField
            ? (ayurField as { value: unknown }).value
            : ayurField;
        if (Array.isArray(ayurMeds)) {
          base.prescribedMedications = ayurMeds
            .map((med: unknown) => toStructuredMedication(med))
            .filter((m): m is SimplifiedMedication => m !== null && m.medication.trim().length > 0);
        }
      }
    }
    // If aiAnalysis is available, use it (secondary source)
    else if (aiAnalysis) {
      base.patientSummary = getFieldValue(aiAnalysis?.patient_summary) || '';
      base.chiefComplaint = getFieldValue(aiAnalysis?.chief_complaint) || '';
      base.clinicalSymptoms = getFieldValue(aiAnalysis?.history_of_present_illness) || '';
      base.medicalHistory = extractMedicalHistory(aiAnalysis);
      base.familyHistory = getFieldValue(aiAnalysis.family_history) || '';
      base.generalExamination = getFieldValue(aiAnalysis?.examination) || '';
      base.investigations = getFieldValue(aiAnalysis?.investigations_orders) || '';
      base.treatmentPlan = getFieldValue(aiAnalysis?.follow_up_details) || '';
      base.doctorNotes = getFieldValue(aiAnalysis?.doctor_notes) || '';
      base.assessmentDiagnosis = getFieldValue(aiAnalysis?.diagnosis) || '';
      base.medicationHistory = getFieldValue(aiAnalysis?.medication_history) || '';
      base.dietAdvice = getFieldValue(aiAnalysis?.diet_advice) || '';
      base.prescribedMedications = cloneMedications(prescribedMedications);
    } else if (dbSummary) {
      // DB summary (persisted on previous load or save)
      base.patientSummary = dbSummary.patientSummary || '';
      base.chiefComplaint = dbSummary.chiefComplaint || '';
      base.clinicalSymptoms = dbSummary.clinicalSymptoms || '';
      base.medicalHistory = dbSummary.medicalHistory || '';
      base.familyHistory = dbSummary.familyHistory || '';
      base.generalExamination = dbSummary.generalExamination || '';
      base.investigations =
        (typeof dbSummary.investigations === 'string' ? dbSummary.investigations : '') || '';
      base.treatmentPlan = dbSummary.treatmentPlan || '';
      base.doctorNotes = dbSummary.doctorNotes || '';
      base.allergies = dbSummary.allergies || '';
      base.socialHistory = dbSummary.socialHistory || '';
      base.reviewOfSystems = dbSummary.reviewOfSystems || '';
      base.followUpPlan = dbSummary.followUpPlan || '';
      base.assessmentDiagnosis = dbSummary.assessmentDiagnosis || '';
      base.pediatricMilestone = dbSummary.pediatricMilestone || '';
      base.adtOrder = dbSummary.adtOrder || '';
      base.medicationHistory = dbSummary.medicationHistory || '';
      base.dietAdvice = dbSummary.dietAdvice || '';
      base.prescribedMedications =
        dbMedications.length > 0
          ? cloneMedications(dbMedications)
          : cloneMedications(prescribedMedications);

      // --- Merge AyurvedaData fields if present and DB summary fields are empty ---
      if (latestReportConclusion) {
        const ayurvedaData = parseAyurvedaFromConclusion(latestReportConclusion);
        if (ayurvedaData) {
          // Merge knownDiseases into medicalHistory if DB summary field is empty
          if (
            !base.medicalHistory &&
            ayurvedaData.knownDiseases &&
            ayurvedaData.knownDiseases.length > 0
          ) {
            base.medicalHistory = ayurvedaData.knownDiseases.join('. ');
          }
          // Merge familyHistory if DB summary field is empty
          if (
            !base.familyHistory &&
            ayurvedaData.familyHistory &&
            ayurvedaData.familyHistory.length > 0
          ) {
            base.familyHistory = ayurvedaData.familyHistory.join('. ');
          }
        }
      }
    } else if (latestReportConclusion) {
      // Fallback: Parse from DiagnosticReport conclusion (JSON format)
      const parsedFields = parseJsonConclusion(latestReportConclusion);
      base.patientSummary = parsedFields.patientSummary || '';
      base.chiefComplaint = parsedFields.chiefComplaint || '';
      base.clinicalSymptoms = parsedFields.clinicalSymptoms || '';
      base.medicalHistory = parsedFields.medicalHistory || '';
      base.familyHistory = parsedFields.familyHistory || '';
      base.generalExamination = parsedFields.generalExamination || '';
      base.treatmentPlan = parsedFields.treatmentPlan || '';
      base.doctorNotes = parsedFields.doctorNotes || '';
      base.allergies = parsedFields.allergies || '';
      base.socialHistory = parsedFields.socialHistory || '';
      base.reviewOfSystems = parsedFields.reviewOfSystems || '';
      base.followUpPlan = parsedFields.followUpPlan || '';
      base.assessmentDiagnosis = parsedFields.assessmentDiagnosis || '';
      base.pediatricMilestone = parsedFields.pediatricMilestone || '';
      base.adtOrder = parsedFields.adtOrder || '';
      base.medicationHistory = parsedFields.medicationHistory || '';
      base.dietAdvice = parsedFields.dietAdvice || '';
      base.prescribedMedications = parsedFields.medications
        ? cloneMedications(parsedFields.medications)
        : cloneMedications(prescribedMedications);

      // --- Merge AyurvedaData fields if present ---
      const ayurvedaData = parseAyurvedaFromConclusion(latestReportConclusion);
      if (ayurvedaData) {
        // Merge knownDiseases into medicalHistory if not already present
        if (ayurvedaData.knownDiseases && ayurvedaData.knownDiseases.length > 0) {
          const knownDiseasesText = ayurvedaData.knownDiseases.join('. ');
          if (!base.medicalHistory.includes(knownDiseasesText)) {
            base.medicalHistory = base.medicalHistory
              ? base.medicalHistory + '. ' + knownDiseasesText
              : knownDiseasesText;
          }
        }
        // Merge familyHistory if not already present
        if (ayurvedaData.familyHistory && ayurvedaData.familyHistory.length > 0) {
          const familyHistoryText = ayurvedaData.familyHistory.join('. ');
          if (!base.familyHistory.includes(familyHistoryText)) {
            base.familyHistory = base.familyHistory
              ? base.familyHistory + '. ' + familyHistoryText
              : familyHistoryText;
          }
        }
      }
    }

    // Ayurveda PA data is often stored as a DiagnosticReport JSON with keys like
    // treatment_plan, known_diseases_history, lifestyle, and viruddha_ahaara.
    // Merge it field-by-field so empty session/DB values don't hide real PA data.
    if (latestReportConclusion) {
      const parsedFields = parseJsonConclusion(latestReportConclusion);
      base.patientSummary ||= parsedFields.patientSummary || '';
      base.chiefComplaint ||= parsedFields.chiefComplaint || '';
      base.clinicalSymptoms ||= parsedFields.clinicalSymptoms || '';
      base.medicalHistory ||= parsedFields.medicalHistory || '';
      base.familyHistory ||= parsedFields.familyHistory || '';
      base.generalExamination ||= parsedFields.generalExamination || '';
      base.treatmentPlan ||= parsedFields.treatmentPlan || '';
      base.doctorNotes ||= parsedFields.doctorNotes || '';
      base.allergies ||= parsedFields.allergies || '';
      base.socialHistory ||= parsedFields.socialHistory || '';
      base.reviewOfSystems ||= parsedFields.reviewOfSystems || '';
      base.followUpPlan ||= parsedFields.followUpPlan || '';
      base.assessmentDiagnosis ||= parsedFields.assessmentDiagnosis || '';
      base.pediatricMilestone ||= parsedFields.pediatricMilestone || '';
      base.adtOrder ||= parsedFields.adtOrder || '';
      base.medicationHistory ||= parsedFields.medicationHistory || '';
      base.dietAdvice ||= parsedFields.dietAdvice || '';
      if (base.prescribedMedications.length === 0 && parsedFields.medications?.length) {
        base.prescribedMedications = cloneMedications(parsedFields.medications);
      }
    }

    // If medications weren't set from sessionSummary, use prescribedMedications
    if (base.prescribedMedications.length === 0) {
      base.prescribedMedications = cloneMedications(prescribedMedications);
    }

    // Ayurveda Dosha Profile — includes Prakriti, Vikriti, Agni, Ama when available
    if (!base.doshaProfile) {
      const doshaFromSession = sessionSummary?.dosha_profile as
        | Record<string, unknown>
        | string
        | null
        | undefined;
      if (typeof doshaFromSession === 'string') {
        base.doshaProfile =
          tryConvertLabelledPairsToBulletHtml(doshaFromSession) ?? doshaFromSession;
      } else if (doshaFromSession && typeof doshaFromSession === 'object') {
        const p = coerceToText(doshaFromSession.prakriti);
        const v = coerceToText(doshaFromSession.vikriti);
        const agni = coerceToText(doshaFromSession.agni_status);
        const ama = coerceToText(doshaFromSession.ama_grade);
        const doshaItems = [
          p && `<li><strong>Prakriti</strong>: ${p}</li>`,
          v && `<li><strong>Vikriti</strong>: ${v}</li>`,
          agni && `<li><strong>Agni</strong>: ${agni}</li>`,
          ama && `<li><strong>Ama</strong>: ${ama}</li>`,
        ].filter(Boolean);
        if (doshaItems.length > 0) base.doshaProfile = `<ul>${doshaItems.join('')}</ul>`;
      }
    }
    if (!base.doshaProfile && latestReportConclusion) {
      try {
        const reportJson = JSON.parse(latestReportConclusion) as Record<string, unknown>;
        const doshaFromReport = reportJson?.dosha_profile as
          | Record<string, unknown>
          | string
          | null
          | undefined;
        if (typeof doshaFromReport === 'string') {
          base.doshaProfile =
            tryConvertLabelledPairsToBulletHtml(doshaFromReport) ?? doshaFromReport;
        } else if (doshaFromReport && typeof doshaFromReport === 'object') {
          const p = coerceToText(doshaFromReport.prakriti);
          const v = coerceToText(doshaFromReport.vikriti);
          const agni = coerceToText(doshaFromReport.agni_status);
          const ama = coerceToText(doshaFromReport.ama_grade);
          const doshaItems = [
            p && `<li><strong>Prakriti</strong>: ${p}</li>`,
            v && `<li><strong>Vikriti</strong>: ${v}</li>`,
            agni && `<li><strong>Agni</strong>: ${agni}</li>`,
            ama && `<li><strong>Ama</strong>: ${ama}</li>`,
          ].filter(Boolean);
          if (doshaItems.length > 0) base.doshaProfile = `<ul>${doshaItems.join('')}</ul>`;
        }
      } catch {
        // malformed JSON — ignore
      }
    }

    // Final dosha fallback: Gemini sometimes places dosha info (e.g. "Kapha body type")
    // inside assessment_and_diagnosis rather than the structured dosha_profile fields.
    // Scan the assessment text for any dosha keyword and use it when nothing else matched.
    if (!base.doshaProfile && base.assessmentDiagnosis) {
      const _assessment = base.assessmentDiagnosis || '';
      const _DOSHA_KW = ['kapha', 'vata', 'pitta', 'tridosha', 'prakriti', 'vikriti', 'dosha'];
      if (_DOSHA_KW.some((kw) => _assessment.toLowerCase().includes(kw))) {
        base.doshaProfile = _assessment;
      }
    }

    // Astha Sthaana Pareeksha + Prakruthi/Dosha/Aama. Priority order:
    // 1. clinicalAssessment passed directly from ConsultationPage's
    //    effectiveClinicalAssessment (navigation state) — the exact same,
    //    already-verified-correct data shown on the consultation page's
    //    Ayurvedic Assessment card (live entries + kiosk-snapshot fallback
    //    already merged there).
    // 2. The saved DiagnosticReport conclusion's astha_sthaana_pareeksha/
    //    prakruthi_dosha JSON objects — used when reopening this Case Sheet
    //    later (e.g. after a refresh), when the navigation state is gone.
    const asthaSthaanaValues: Record<string, string | null | undefined> = {};
    if (Array.isArray(passedClinicalAssessment)) {
      for (const entry of passedClinicalAssessment) {
        if (entry?.parameter && entry.value) {
          asthaSthaanaValues[entry.parameter] = entry.value;
        }
      }
    }
    if (Object.keys(asthaSthaanaValues).length === 0 && latestReportConclusion) {
      try {
        const reportJson = JSON.parse(latestReportConclusion) as Record<string, unknown>;
        const astha = reportJson?.astha_sthaana_pareeksha as Record<string, unknown> | undefined;
        if (astha && typeof astha === 'object') {
          for (const key of [
            'nadi',
            'mootram',
            'malam',
            'jihva',
            'sabdam',
            'sparsham',
            'drik',
            'akrithi',
          ]) {
            const value = coerceToText(astha[key]);
            if (value) asthaSthaanaValues[key] = value;
          }
        }
        const prakruthiDosha = reportJson?.prakruthi_dosha as Record<string, unknown> | undefined;
        if (prakruthiDosha && typeof prakruthiDosha === 'object') {
          for (const key of ['prakruthi', 'dosha', 'aama']) {
            const value = coerceToText(prakruthiDosha[key]);
            if (value) asthaSthaanaValues[key] = value;
          }
        }
      } catch {
        // malformed JSON — ignore
      }
    }
    // 3. Independently-fetched kiosk Composition (same source as Duration/
    //    Mode of Onset above) — covers a reopened Case Sheet whose saved
    //    conclusion JSON predates this feature and has neither key above.
    if (Object.keys(asthaSthaanaValues).length === 0 && kioskAyurvedaData) {
      const astha = kioskAyurvedaData.asthaSthaanaPareeksha;
      const prakruthiDosha = kioskAyurvedaData.prakruthiDosha;
      if (astha) {
        for (const key of [
          'nadi',
          'mootram',
          'malam',
          'jihva',
          'sabdam',
          'sparsham',
          'drik',
          'akrithi',
        ] as const) {
          if (astha[key]) asthaSthaanaValues[key] = astha[key];
        }
      }
      if (prakruthiDosha) {
        for (const key of ['prakruthi', 'dosha', 'aama'] as const) {
          if (prakruthiDosha[key]) asthaSthaanaValues[key] = prakruthiDosha[key];
        }
      }
    }
    const asthaSthaanaItems = buildAsthaSthaanaPareekshaItems(asthaSthaanaValues);
    if (asthaSthaanaItems.length > 0) {
      if (base.doshaProfile?.trim().startsWith('<ul>')) {
        // Splice the Astha Sthaana bullets into the same <ul> the Prakriti/
        // Vikriti/Agni/Ama bullets are already in, so everything shows as one
        // unified bullet list rather than two separate lists.
        base.doshaProfile = base.doshaProfile.replace(
          '</ul>',
          `${asthaSthaanaItems.join('')}</ul>`
        );
      } else {
        const asthaSthaanaHtml = `<ul>${asthaSthaanaItems.join('')}</ul>`;
        base.doshaProfile = base.doshaProfile
          ? `<p>${base.doshaProfile}</p>${asthaSthaanaHtml}`
          : asthaSthaanaHtml;
      }
    }

    // Diagnosis/Treatment — seed the now-editable fields from the same
    // priority-ordered values already computed above (passed clinical
    // assessment → saved report → kiosk Composition), same as
    // doshaProfile/Astha Sthaana Pareeksha did.
    base.diagnosisAyurvedic = diagnosisAyurvedic ?? '';
    base.diagnosisAllopathic = diagnosisAllopathic ?? '';
    base.treatmentMedicines = treatmentMedicines ?? '';
    base.treatmentMedicinesTable = treatmentMedicinesTable ?? [];
    base.treatmentPrinciple = treatmentPrinciple ?? '';
    base.treatmentGiven = treatmentGiven ?? '';
    base.personalHistory = personalHistory ? { ...personalHistory } : undefined;

    return base;
  }, [
    sessionSummary,
    aiAnalysis,
    diagnosisAyurvedic,
    diagnosisAllopathic,
    treatmentMedicines,
    treatmentMedicinesTable,
    treatmentPrinciple,
    treatmentGiven,
    personalHistory,
    prescribedMedications,
    dbSummary,
    dbMedications,
    latestReportConclusion,
    extractMedicalHistory,
    getFieldValue,
    getSessionFieldValue,
    parseJsonConclusion,
    passedClinicalAssessment,
    kioskAyurvedaData,
  ]);

  useEffect(() => {
    // Don't initialize if DiagnosticReport is still loading and we don't have aiAnalysis or sessionSummary
    // This prevents initializing with empty fields when data is still loading
    if (
      !aiAnalysis &&
      !sessionSummary &&
      (isLoadingEncounterDiagnosticReport || isLoadingFhirSummary || isLoadingDbSummary)
    ) {
      return;
    }

    if (!editableFields) {
      setEditableFields(cloneEditableFields(initialEditableFields));
      return;
    }

    if (hasUserEditedFields) {
      return;
    }

    // Update editableFields when initialEditableFields gets populated (e.g., when DiagnosticReport loads on refresh)
    // This handles the case where editableFields was initialized as empty before DiagnosticReport loaded
    if (!fieldsHaveContent(editableFields) && fieldsHaveContent(initialEditableFields)) {
      setEditableFields(cloneEditableFields(initialEditableFields));
    }
  }, [
    editableFields,
    initialEditableFields,
    hasUserEditedFields,
    encounterDiagnosticReport,
    aiAnalysis,
    sessionSummary,
    isLoadingEncounterDiagnosticReport,
    isLoadingFhirSummary,
    isLoadingDbSummary,
  ]);

  const fieldsForView = editableFields ?? cloneEditableFields(initialEditableFields);

  // Build aiAnalysis with pranik_cdss from sessionSummary if available
  // This ensures the CDSS data is passed to CaseSheetView regardless of source
  const effectiveAIAnalysis = useMemo(() => {
    // Start with aiAnalysis if available, otherwise empty object
    const base = aiAnalysis || ({} as AIAnalysisResult);

    // If we have sessionSummary, merge relevant fields
    if (sessionSummary) {
      return {
        ...base,
        // Merge pranik_cdss from sessionSummary if not already present
        pranik_cdss: base.pranik_cdss || sessionSummary.pranik_cdss,
        // Merge investigations_orders from sessionSummary if not already present
        investigations_orders: base.investigations_orders || sessionSummary.investigations_orders,
        // Merge other fields from sessionSummary that might be useful
        chief_complaint: base.chief_complaint || sessionSummary.chief_complaint,
        history_of_present_illness:
          base.history_of_present_illness || sessionSummary.history_of_present_illness,
        patient_summary: base.patient_summary || sessionSummary.patient_summary,
        family_history: base.family_history || sessionSummary.family_history,
        follow_up_details:
          base.follow_up_details ||
          sessionSummary.follow_up_plan ||
          sessionSummary.treatment_plan_management,
        diagnosis: base.diagnosis || sessionSummary.assessment_and_diagnosis,
        medications: base.medications || sessionSummary.medications,
        examination: base.examination || sessionSummary.physical_examination,
        medical_history:
          base.medical_history ||
          sessionSummary.past_medical_surgical_history ||
          sessionSummary.medication_history,
        // Add SessionSummary-specific fields for timestamp mapping
        treatment_plan_management: sessionSummary.treatment_plan_management,
        follow_up_plan: sessionSummary.follow_up_plan,
        past_medical_surgical_history: sessionSummary.past_medical_surgical_history,
      } as AIAnalysisResult;
    }

    // Return base aiAnalysis or undefined if nothing available
    return aiAnalysis ? base : undefined;
  }, [aiAnalysis, sessionSummary]);

  // On refresh, rebuild effectiveAIAnalysis from dbSummary when no live data is available
  const resolvedAIAnalysis = useMemo(() => {
    if (effectiveAIAnalysis) return effectiveAIAnalysis;
    if (!dbSummary) return undefined;

    // Reconstruct a minimal AIAnalysisResult from DB data for the view
    return {
      investigations_orders: dbSummary.investigations
        ? { value: dbSummary.investigations, timestamp: '', explanation: '' }
        : undefined,
      pranik_cdss: dbSummary.pranikCdss as PranikCDSS | undefined,
      diagnosis: dbSummary.assessmentDiagnosis
        ? { value: dbSummary.assessmentDiagnosis, timestamp: '', explanation: '' }
        : undefined,
      follow_up_details: dbSummary.followUpPlan
        ? { value: dbSummary.followUpPlan, timestamp: '', explanation: '' }
        : undefined,
      // Restore ICD-11 symptom codes from DB so Diagnosis table survives refresh
      history_of_present_illness: dbSummary.diagnosisCodes?.length
        ? {
            value: dbSummary.clinicalSymptoms || '',
            timestamp: '',
            explanation: '',
            symptom_codes: dbSummary.diagnosisCodes,
          }
        : dbSummary.clinicalSymptoms
          ? { value: dbSummary.clinicalSymptoms, timestamp: '', explanation: '' }
          : undefined,
    } as AIAnalysisResult;
  }, [effectiveAIAnalysis, dbSummary]);

  // Shared helper — builds the CaseSheetSummaryData payload from current editable fields.
  // Used by both the debounced field-edit sync and the final "Save record" mutation.
  const buildDbPayload = useCallback(
    (encounterId: string, fields: CaseSheetEditableFields) => ({
      encounterId,
      practitionerId: user?.id || '',
      patientId: patientId || '',
      patientSummary: fields.patientSummary.trim(),
      chiefComplaint: fields.chiefComplaint.trim(),
      clinicalSymptoms: fields.clinicalSymptoms.trim(),
      medicalHistory: fields.medicalHistory.trim(),
      familyHistory: fields.familyHistory.trim(),
      generalExamination: fields.generalExamination.trim(),
      treatmentPlan: fields.treatmentPlan.trim(),
      doctorNotes: fields.doctorNotes.trim(),
      medications: fields.prescribedMedications.filter((m) => m.medication.trim()),
      assessmentDiagnosis: (fields.assessmentDiagnosis || '').trim(),
      socialHistory: (fields.socialHistory || '').trim(),
      reviewOfSystems: (fields.reviewOfSystems || '').trim(),
      allergies: (fields.allergies || '').trim(),
      followUpPlan: (fields.followUpPlan || '').trim(),
      pediatricMilestone: (fields.pediatricMilestone || '').trim(),
      adtOrder: (fields.adtOrder || '').trim(),
      medicationHistory: (fields.medicationHistory || '').trim(),
      dietAdvice: (fields.dietAdvice || '').trim(),
      investigations:
        fields.investigations.trim() ||
        ((sessionSummary?.investigations_orders
          ? typeof sessionSummary.investigations_orders === 'string'
            ? sessionSummary.investigations_orders
            : (sessionSummary.investigations_orders as { value?: string })?.value || ''
          : (dbSummary?.investigations as unknown)) as unknown),
      pranikCdss: sessionSummary?.pranik_cdss as Record<string, unknown> | undefined,
      // Persist ICD-11 symptom codes so they survive a page refresh. User edits made in the
      // Diagnosis table (fields.diagnosisCodes) take priority over the AI-extracted codes.
      // Items are normalized/validated before saving to prevent malformed data reaching the UI.
      diagnosisCodes: (() => {
        if (fields.diagnosisCodes && fields.diagnosisCodes.length > 0) {
          return fields.diagnosisCodes
            .map((item) => ({
              symptom: item.symptom.trim(),
              symptom_id: [item.type?.trim() || 'ICD-10', item.code.trim(), item.description.trim()]
                .join('|')
                .replace(/\|+$/, ''),
            }))
            .filter((item) => item.symptom.length > 0);
        }

        const hpi = effectiveAIAnalysis?.history_of_present_illness;
        if (!hpi || typeof hpi !== 'object') return dbSummary?.diagnosisCodes;
        const raw = (hpi as unknown as Record<string, unknown>).symptom_codes;
        if (!Array.isArray(raw) || raw.length === 0) return dbSummary?.diagnosisCodes;
        const normalized = raw
          .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
          .map((item) => ({
            symptom: typeof item.symptom === 'string' ? item.symptom.trim() : '',
            symptom_id: typeof item.symptom_id === 'string' ? item.symptom_id.trim() : '',
          }))
          .filter((item) => item.symptom.length > 0);
        return normalized.length > 0 ? normalized : dbSummary?.diagnosisCodes;
      })(),
    }),
    [user?.id, patientId, dbSummary, sessionSummary, getSessionFieldValue, effectiveAIAnalysis]
  );

  const handleFieldChange = useCallback(
    <K extends keyof CaseSheetEditableFields>(field: K, value: CaseSheetEditableFields[K]) => {
      setEditableFields((prev) => {
        const reference = prev ?? initialEditableFields ?? createEmptyEditableFields();
        const next = cloneEditableFields(reference);

        if (field === 'prescribedMedications') {
          next.prescribedMedications = cloneMedications(value as SimplifiedMedication[]);
        } else if (field === 'diagnosisCodes') {
          next.diagnosisCodes = cloneDiagnosisCodes(value as DiagnosisCodeItem[]);
        } else if (field === 'treatmentMedicinesTable') {
          next.treatmentMedicinesTable = (value as PrescribedMedicine[]).map((med) => ({
            ...med,
          }));
        } else if (field === 'personalHistory') {
          next.personalHistory = { ...(value as PersonalHistory) };
        } else {
          const key = field as TextFieldKey;
          next[key] = value as string;
        }

        return next;
      });
      setHasUserEditedFields(true);
    },
    [initialEditableFields]
  );

  // Debounced DB sync whenever the user edits a field.
  // Keeps the DB in sync so a page refresh always shows the latest edits.
  const fieldSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasPerformedInitialSave = useRef(false);

  // Once the record is filed (Save Record deletes the temp DB row) autosave must stop,
  // or it would recreate that row.
  const recordSaved = isSaved || linkedAppointment?.status === 'fulfilled';
  const recordSavedRef = useRef(recordSaved);
  recordSavedRef.current = recordSaved;

  // Bumped on every edit; lets a finished DB save tell whether newer edits arrived meanwhile.
  const editVersion = useRef(0);
  // Autosaves run strictly one after another so an older request can never land after,
  // and overwrite, a newer one.
  const saveQueue = useRef<Promise<unknown>>(Promise.resolve());

  // Single path for every DB autosave: drives the header status and drops the browser
  // backup once the DB is known to hold the latest edits.
  const persistToDb = useCallback(
    async (fields: CaseSheetEditableFields, keepalive = false) => {
      if (!consultationId || !user?.id || recordSavedRef.current) return;
      const version = editVersion.current;
      if (!keepalive) setAutosave((prev) => ({ ...prev, status: 'saving' }));
      const payload = buildDbPayload(consultationId, fields);
      try {
        // keepalive flushes run while the page is closing, so they can't wait in the queue.
        if (keepalive) {
          await caseSheetSummaryApi.saveSummary(payload, { keepalive: true });
        } else {
          const run = saveQueue.current
            .catch(() => undefined)
            .then(() => caseSheetSummaryApi.saveSummary(payload));
          saveQueue.current = run;
          await run;
        }
        if (editVersion.current === version && !hasFieldsOutsideDb(fields)) {
          clearCaseSheetBackup(user.id, consultationId);
        }
        setAutosave({ status: 'saved', at: Date.now() });
      } catch (e) {
        setAutosave((prev) => ({ ...prev, status: 'error' }));
        throw e;
      }
    },
    [consultationId, user?.id, buildDbPayload]
  );

  // Browser backup: copy every edit to localStorage straight away (the DB write is debounced
  // and can fail), so a refresh or network drop never loses it.
  useEffect(() => {
    if (!hasUserEditedFields || !editableFields || !consultationId || !user?.id) return;
    if (recordSaved) return;
    editVersion.current += 1;
    writeCaseSheetBackup(user.id, consultationId, editableFields);
  }, [editableFields, hasUserEditedFields, consultationId, user?.id, recordSaved]);

  // On open, restore edits that never reached the DB (the backup is removed once they do).
  useEffect(() => {
    if (backupChecked || !consultationId || !user?.id) return;
    if (isLoadingDbSummary || isLoadingEncounterDiagnosticReport || isLoadingFhirSummary) return;
    setBackupChecked(true);
    if (recordSaved) {
      clearCaseSheetBackup(user.id, consultationId);
      return;
    }
    const backup = readCaseSheetBackup<CaseSheetEditableFields>(user.id, consultationId);
    if (!backup) return;
    setEditableFields(cloneEditableFields(backup));
    setHasUserEditedFields(true);
    toast.info('Restored your unsaved changes from this browser.', { duration: 4000 });
  }, [
    backupChecked,
    consultationId,
    user?.id,
    recordSaved,
    isLoadingDbSummary,
    isLoadingEncounterDiagnosticReport,
    isLoadingFhirSummary,
  ]);

  // One-time save: persist session/AI data to DB when fields are first populated.
  // Without this, data from sessionSummary is only in memory (location.state)
  // and lost on page refresh.
  useEffect(() => {
    if (hasPerformedInitialSave.current) return;
    if (!consultationId || !user?.id) return;
    // Wait for the Medplum fallbacks to settle: persisting whichever arrives first (e.g. an
    // older DiagnosticReport) would make that stale row authoritative on the next open.
    // Also wait for the browser-backup check so restored edits aren't overwritten first.
    if (!backupChecked) return;
    if (isLoadingDbSummary || isLoadingEncounterDiagnosticReport || isLoadingFhirSummary) return;
    const fields = editableFields ?? initialEditableFields;
    if (!fields || !fieldsHaveContent(fields)) return;

    hasPerformedInitialSave.current = true;
    persistToDb(fields).catch((e) => {
      logger.error('Failed initial save of session data to DB:', e);
      hasPerformedInitialSave.current = false; // retry when inputs next change
    });
  }, [
    editableFields,
    initialEditableFields,
    consultationId,
    user?.id,
    persistToDb,
    backupChecked,
    isLoadingDbSummary,
    isLoadingEncounterDiagnosticReport,
    isLoadingFhirSummary,
  ]);

  useEffect(() => {
    if (!hasUserEditedFields) return;
    if (!consultationId || !user?.id) return;

    if (fieldSaveTimer.current) clearTimeout(fieldSaveTimer.current);

    fieldSaveTimer.current = setTimeout(() => {
      fieldSaveTimer.current = null;
      if (!editableFields) return;
      persistToDb(editableFields).catch((e) => logger.error('Failed to sync field edit to DB:', e));
    }, 1500);

    return () => {
      if (fieldSaveTimer.current) clearTimeout(fieldSaveTimer.current);
    };
  }, [editableFields, hasUserEditedFields, consultationId, user?.id, persistToDb]);

  const latestAutosave = useRef<(() => void) | null>(null);
  latestAutosave.current = () => {
    if (!hasUserEditedFields || !consultationId || !user?.id) return;
    const fields = editableFields ?? initialEditableFields;
    if (!fields || !fieldSaveTimer.current) return;
    clearTimeout(fieldSaveTimer.current);
    fieldSaveTimer.current = null;
    persistToDb(fields, true).catch((e) =>
      logger.error('Failed to flush case sheet on page hide:', e)
    );
  };
  useEffect(() => {
    const flush = () => latestAutosave.current?.();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const handleAddMedication = useCallback(
    (medication: SimplifiedMedication) => {
      setEditableFields((prev) => {
        const reference = prev ?? initialEditableFields ?? createEmptyEditableFields();
        const next = cloneEditableFields(reference);

        // Add the medication to the list, avoiding duplicates (case-insensitive by name)
        const existingIndex = next.prescribedMedications.findIndex(
          (med) => med.medication.toLowerCase() === medication.medication.toLowerCase()
        );

        if (existingIndex === -1) {
          next.prescribedMedications = [...next.prescribedMedications, medication];
          toast.success(`Added ${medication.medication}`, { duration: 2000 });
        } else {
          toast.info(`${medication.medication} already exists`, { duration: 2000 });
        }

        return next;
      });
      setHasUserEditedFields(true);
    },
    [initialEditableFields]
  );

  const handleReplaceMedication = useCallback(
    (
      _originalMedicationId: string,
      originalMedicationName: string,
      replacement: SimplifiedMedication
    ) => {
      setEditableFields((prev) => {
        const reference = prev ?? initialEditableFields ?? createEmptyEditableFields();
        const next = cloneEditableFields(reference);

        // Find the medication to replace by name (case-insensitive)
        const indexToReplace = next.prescribedMedications.findIndex((med) =>
          med.medication.toLowerCase().includes(originalMedicationName.toLowerCase())
        );

        if (indexToReplace !== -1) {
          // Check if replacement already exists (avoid duplicates)
          const replacementExists = next.prescribedMedications.some(
            (med, idx) =>
              idx !== indexToReplace &&
              med.medication.toLowerCase() === replacement.medication.toLowerCase()
          );

          if (replacementExists) {
            next.prescribedMedications = next.prescribedMedications.filter(
              (_, idx) => idx !== indexToReplace
            );
            toast.success(`Removed ${originalMedicationName}`, { duration: 2000 });
          } else {
            next.prescribedMedications[indexToReplace] = replacement;
            toast.success(`Replaced with ${replacement.medication}`, { duration: 2000 });
          }
        } else {
          const replacementExists = next.prescribedMedications.some(
            (med) => med.medication.toLowerCase() === replacement.medication.toLowerCase()
          );

          if (!replacementExists) {
            next.prescribedMedications = [...next.prescribedMedications, replacement];
            toast.success(`Added ${replacement.medication}`, { duration: 2000 });
          } else {
            toast.info(`${replacement.medication} already exists`, { duration: 2000 });
          }
        }

        return next;
      });
      setHasUserEditedFields(true);
    },
    [initialEditableFields]
  );

  // Mutation to save the case sheet summary (DB + FHIR) and update encounter/appointment status
  const saveMutation = useMutation({
    mutationFn: async () => {
      const currentEncounter = encounter;

      if (!currentEncounter) {
        throw new Error('Encounter not found');
      }

      if (!patientId) {
        throw new Error('Patient not found');
      }
      if (!user?.id) {
        throw new Error('Practitioner not found');
      }

      const encounterId = currentEncounter.id!;
      const fields = editableFields ?? initialEditableFields ?? createEmptyEditableFields();

      // 1. Persist the case sheet to the DB first. It's cheap, and it keeps the
      //    doctor's edits recoverable if the FHIR write below fails.
      try {
        await caseSheetSummaryApi.saveSummary(buildDbPayload(encounterId, fields));
      } catch (dbError) {
        logger.error('Failed to save case sheet summary to DB:', dbError);
      }

      // 2. Write the clinical record to Medplum. This creates/updates the
      //    Composition and every resource it ties together, and is an upsert —
      //    safe to call again on a re-save. This is the system of record, so a
      //    failure here must fail the whole save: the doctor needs to retry
      //    rather than believe the visit was filed.
      await caseSheetSummaryApi.saveSummaryToFhir({
        patientId,
        practitionerId: user.id,
        encounterId,
        // Until the doctor edits something, every field is untouched by
        // definition. Comparing against the initial form before then could
        // mistake data that simply hadn't loaded when the form was seeded for
        // a field the doctor cleared.
        payload: buildFhirSummaryPayload(
          fields,
          hasUserEditedFields ? (initialEditableFields ?? fields) : fields,
          sessionSummary,
          dbSummary?.diagnosisCodes
        ),
      });

      // 3. Only once the Composition exists is the visit actually complete, so
      //    the appointment is fulfilled last. A failure here leaves the record
      //    saved but the appointment still open — worth surfacing, not worth
      //    discarding a successful save over.
      try {
        const appointment = await appointmentsApi.getAppointmentForEncounter(encounterId);
        if (appointment && appointment.status !== 'fulfilled') {
          await appointmentsApi.fulfillAppointment(appointment.id);
        }
      } catch (appointmentError) {
        logger.error('Failed to mark appointment fulfilled:', appointmentError);
        toast.warning('Record saved, but the appointment is still marked open.', {
          duration: 4000,
        });
      }
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['encounters', consultationId], exact: false });
      queryClient.invalidateQueries({
        queryKey: ['appointmentForEncounter', consultationId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['dashboard-practitioner-appointments'],
        exact: false,
      });
      queryClient.invalidateQueries({ queryKey: ['appointments'], exact: false });
      queryClient.invalidateQueries({
        queryKey: ['caseSheetSummary', consultationId],
        exact: true,
      });
      setIsSaved(true);
      if (user?.id && consultationId) clearCaseSheetBackup(user.id, consultationId);

      // Clean up draft and temp summary from DB (best-effort, don't block on failure)
      if (consultationId) {
        draftsApi.deleteDraft(consultationId).catch((e) => {
          logger.error('Failed to delete draft after final save:', e);
        });
        caseSheetSummaryApi.deleteSummary(consultationId).catch((e) => {
          logger.error('Failed to delete temp summary after final save:', e);
        });
      }

      // Trigger summary generation API silently (no toast spam)
      if (patientId) {
        try {
          await patientsApi.triggerSummaryGeneration(patientId);
        } catch (error) {
          logger.error('Failed to trigger summary generation:', error);
          // Silent failure - summary generation is background task
        }
      }

      // Single success toast with short duration
      toast.success('Case sheet saved', { duration: 2000 });

      // Navigate to dashboard
      navigate('/dashboard');
    },
    onError: (error) => {
      logger.error('Failed to save case sheet:', error);
      toast.error('Failed to save case sheet', {
        description: getErrorMessage(error),
        duration: 5000,
      });
    },
  });

  // Mutation to save consultation as a draft (CDSS data saved to DB, encounter stays in-progress)
  const draftSaveMutation = useMutation({
    mutationFn: async () => {
      const currentEncounter = encounter;

      if (!currentEncounter) {
        throw new Error('Encounter not found');
      }

      // Mark appointment as pending to indicate draft state (parked consultation)
      try {
        const linkedAppointment = await appointmentsApi.getAppointmentForEncounter(
          currentEncounter.id!
        );
        if (linkedAppointment && linkedAppointment.status !== 'fulfilled') {
          await appointmentsApi.updateAppointment(linkedAppointment.id, {
            status: 'pending',
            encounterId: currentEncounter.id,
          });
        }
      } catch (e) {
        logger.warn('Could not update appointment status for draft:', e);
      }

      const encounterId = currentEncounter.id;
      if (!encounterId) throw new Error('Encounter ID not available');

      // Draft CDSS data is already saved to DB by ConsultationPage before
      // navigating here, so we only need to update encounter/appointment statuses.
      // No need to re-save — this avoids overwriting the real draft with empty
      // data when location.state is lost on page refresh.
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['encounters', consultationId], exact: false });
      queryClient.invalidateQueries({
        queryKey: ['dashboard-practitioner-appointments'],
        exact: false,
      });
      queryClient.invalidateQueries({ queryKey: ['appointments'], exact: false });
      toast.success('Draft saved. You can continue the consultation later.', { duration: 3000 });
      navigate('/dashboard');
    },
    onError: (error) => {
      logger.error('Failed to save draft:', error);
      toast.error('Failed to save draft', { duration: 3000 });
    },
  });

  // Block browser back button navigation - trap user on case sheet page
  useEffect(() => {
    // Push an extra state entry to create a barrier
    window.history.pushState(null, '', window.location.href);

    const handlePopState = (_event: PopStateEvent) => {
      // Immediately go forward to prevent back navigation
      // This happens synchronously and prevents React Router from processing the navigation
      window.history.go(1);
      toast.info('Back is disabled here. Use Save or Save as Draft to leave the case sheet.', {
        id: 'case-sheet-back-blocked',
        duration: 3000,
      });
    };

    // Add event listener for popstate (browser back button)
    window.addEventListener('popstate', handlePopState);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Warn on refresh/close while the doctor has edits that aren't filed to
  // Medplum yet. Edits autosave to the DB after a short debounce, but the
  // clinical record is only complete after Save.
  const hasUnfiledEdits = !recordSaved;
  useEffect(() => {
    if (!hasUnfiledEdits) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnfiledEdits]);

  const latestReportJson = useMemo(() => {
    if (!latestReportConclusion) return null;
    try {
      return JSON.parse(latestReportConclusion) as Record<string, unknown>;
    } catch {
      return null;
    }
  }, [latestReportConclusion]);

  // Handle print
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const buildSummaryInput = useCallback((): CaseSheetSummaryInput => {
    const fieldsForSummary = editableFields ?? cloneEditableFields(initialEditableFields);
    const patientName = getPatientNameFromFhir(patient?.name) || 'Unknown Patient';
    const consultationDate = encounter?.period?.start
      ? new Date(encounter.period.start).toLocaleDateString()
      : new Date().toLocaleDateString();
    return {
      patient: {
        name: patientName,
        identifier: patient?.identifier?.[0]?.value || patient?.id || 'Unknown',
        age: patient?.birthDate
          ? (() => {
              const birthDate = new Date(patient.birthDate);
              const today = new Date();
              let age = today.getFullYear() - birthDate.getFullYear();
              const m = today.getMonth() - birthDate.getMonth();
              if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
              return age;
            })()
          : 0,
        gender: patient?.gender?.toUpperCase() || 'U',
        dateOfBirth: patient?.birthDate,
        phoneNumber: patient?.telecom?.find((t) => t.system === 'phone')?.value,
      },
      consultationDate,
      fields: fieldsForSummary,
      vitals: encounterVitals,
      diet: isAyurveda
        ? firstNonEmptyArray(
            sessionSummary?.diet_recommendations,
            latestReportJson?.diet_recommendations,
            latestReportJson?.commonly_eaten_food
          )
        : undefined,
      lifestyle: isAyurveda
        ? firstNonEmptyArray(
            sessionSummary?.lifestyle_recommendations,
            latestReportJson?.lifestyle_recommendations,
            latestReportJson?.lifestyle
          )
        : undefined,
      viruddhaAhaara: isAyurveda
        ? firstNonEmptyArray(sessionSummary?.viruddha_ahaara, latestReportJson?.viruddha_ahaara)
        : undefined,
      isAyurveda,
      treatmentMedicinesTable: isAyurveda
        ? fieldsForSummary.treatmentMedicinesTable &&
          fieldsForSummary.treatmentMedicinesTable.length > 0
          ? fieldsForSummary.treatmentMedicinesTable
          : treatmentMedicinesTable
        : undefined,
      personalHistory: isAyurveda ? personalHistory : undefined,
    };
  }, [
    editableFields,
    initialEditableFields,
    patient,
    encounter,
    isAyurveda,
    sessionSummary,
    latestReportJson,
    encounterVitals,
    treatmentMedicinesTable,
    personalHistory,
  ]);

  const handleCopy = useCallback(() => {
    const text = buildCaseSheetSummaryText(buildSummaryInput());
    if (!navigator?.clipboard) {
      toast.error('Clipboard copy is not supported in this browser context');
      return;
    }
    const copyPromise = navigator.clipboard.writeText(text);
    toast.promise(copyPromise, {
      loading: 'Copying summary...',
      success: 'Case sheet copied to clipboard',
      error: 'Failed to copy to clipboard',
    });
  }, [buildSummaryInput]);

  const handleExport = useCallback(() => {
    const exportPromise = generateCaseSheetPDF(buildSummaryInput());
    toast.promise(exportPromise, {
      loading: 'Generating PDF...',
      success: 'PDF generated',
      error: 'Failed to generate PDF',
    });
  }, [buildSummaryInput]);

  // Handle save
  const handleSave = useCallback(() => {
    saveMutation.mutate();
  }, [saveMutation]);

  // Handle save as draft
  const handleSaveDraft = useCallback(() => {
    draftSaveMutation.mutate();
  }, [draftSaveMutation]);

  // --- Smart Voice Note ---
  const getCurrentSummary = useCallback((): Record<string, unknown> => {
    const fields = editableFields ?? initialEditableFields ?? createEmptyEditableFields();
    return buildVoiceSummaryPayload(fields, sessionSummary);
  }, [editableFields, initialEditableFields, sessionSummary]);

  const handleVoiceSummaryUpdated = useCallback(
    (response: VoiceSmartUpdateResponse) => {
      setVoiceNoteError(null);
      const updated = response.updatedSummary;
      const transcribedText = response.transcribedText?.trim() || '';
      if (!updated && !transcribedText) return;
      const transcriptionOnly = (response.changes?.length ?? 0) === 0;

      setEditableFields((prev) => {
        const base = prev
          ? cloneEditableFields(prev)
          : cloneEditableFields(initialEditableFields ?? createEmptyEditableFields());

        // Only overwrite fields that the AI actually changed
        const patientSummary = getSessionFieldValue(updated.patient_summary);
        if (patientSummary) base.patientSummary = patientSummary;

        const chiefComplaint = getSessionFieldValue(updated.chief_complaint);
        if (chiefComplaint) base.chiefComplaint = chiefComplaint;

        const clinicalSymptoms = getSessionFieldValue(updated.history_of_present_illness);
        if (clinicalSymptoms) base.clinicalSymptoms = clinicalSymptoms;

        const medicalHistory = getSessionFieldValue(updated.past_medical_surgical_history);
        if (medicalHistory) base.medicalHistory = medicalHistory;

        const familyHistory = getSessionFieldValue(updated.family_history);
        if (familyHistory) base.familyHistory = familyHistory;

        const generalExamination = getSessionFieldValue(updated.physical_examination);
        if (generalExamination) base.generalExamination = generalExamination;

        const treatmentPlan = getSessionFieldValue(updated.treatment_plan_management);
        if (treatmentPlan) base.treatmentPlan = treatmentPlan;

        // The raw transcription is kept out of Doctor's Notes whenever the AI produced a
        // structured update (the notes below come from that update). Only when nothing
        // was updated is it appended, so a dictation is never silently lost.
        const doctorNotes = getSessionFieldValue(updated.doctor_notes ?? updated.follow_up_plan);
        if (doctorNotes) {
          base.doctorNotes = doctorNotes;
        } else if (transcriptionOnly && transcribedText) {
          const existingNotes = base.doctorNotes?.trim() || '';
          if (!existingNotes.includes(transcribedText)) {
            base.doctorNotes = existingNotes
              ? `${existingNotes}

${transcribedText}`
              : transcribedText;
          }
        }

        const allergies = getSessionFieldValue(updated.allergies);
        if (allergies) base.allergies = allergies;

        const socialHistory = getSessionFieldValue(updated.social_history);
        if (socialHistory) base.socialHistory = socialHistory;

        const reviewOfSystems = getSessionFieldValue(updated.review_of_systems);
        if (reviewOfSystems) base.reviewOfSystems = reviewOfSystems;

        const followUpPlan = getSessionFieldValue(updated.follow_up_plan ?? updated.doctor_notes);
        if (followUpPlan) base.followUpPlan = followUpPlan;

        const assessmentDiagnosis = getSessionFieldValue(updated.assessment_and_diagnosis);
        if (assessmentDiagnosis) base.assessmentDiagnosis = assessmentDiagnosis;

        const pediatricMilestone = getSessionFieldValue(updated.pediatric_milestone);
        if (pediatricMilestone) base.pediatricMilestone = pediatricMilestone;

        const adtOrder = getSessionFieldValue(
          (updated as Record<string, unknown>).adt_order ??
            (updated as Record<string, unknown>).adt_orders ??
            (updated as Record<string, unknown>).adtOrder
        );
        if (adtOrder) base.adtOrder = adtOrder;

        const medicationHistory = getSessionFieldValue(updated.medication_history);
        if (medicationHistory) base.medicationHistory = medicationHistory;

        const dietAdvice = getSessionFieldValue(
          (updated as Record<string, unknown>).diet_advice ??
            (updated as Record<string, unknown>).meal_plan ??
            (updated as Record<string, unknown>).dietAdvice
        );
        if (dietAdvice) base.dietAdvice = dietAdvice;

        const doshaProfile = getSessionFieldValue(
          (updated as Record<string, unknown>).dosha_profile ??
            (updated as Record<string, unknown>).doshaProfile
        );
        if (doshaProfile) base.doshaProfile = doshaProfile;

        const investigations = getSessionFieldValue(
          (updated as Record<string, unknown>).investigations_orders ??
            (updated as Record<string, unknown>).relevant_investigations
        );
        if (investigations) base.investigations = investigations;

        // Handle medications
        const medsField = updated.medications;
        if (medsField) {
          let medsValue: unknown = medsField;
          if (typeof medsField === 'object' && 'value' in (medsField as object)) {
            medsValue = (medsField as { value: unknown }).value;
          }
          if (Array.isArray(medsValue)) {
            const newMeds = medsValue
              .map((m) => toStructuredMedication(m))
              .filter(
                (m): m is SimplifiedMedication => m !== null && m.medication.trim().length > 0
              );
            if (newMeds.length > 0) {
              // Voice-note transcription rarely produces itemCode/relationWithMeal/
              // additionalInfo/composition — preserve those from the existing entry
              // (matched by medication name) instead of wiping them out.
              const existingByName = new Map(
                base.prescribedMedications.map((med) => [med.medication.trim().toLowerCase(), med])
              );
              base.prescribedMedications = newMeds.map((med) => {
                const existing = existingByName.get(med.medication.trim().toLowerCase());
                if (!existing) return med;
                return {
                  ...med,
                  relationWithMeal: med.relationWithMeal || existing.relationWithMeal,
                  additionalInfo: med.additionalInfo || existing.additionalInfo,
                  itemCode: med.itemCode || existing.itemCode,
                  composition: med.composition || existing.composition,
                  stock: med.stock || existing.stock,
                };
              });
            }
          }
        }

        return base;
      });
      setHasUserEditedFields(true);

      const changeCount = response.changes?.length ?? 0;
      toast.success(
        changeCount > 0
          ? `Voice note applied — ${changeCount} field${changeCount !== 1 ? 's' : ''} updated`
          : transcribedText
            ? 'Voice note transcription added to the case sheet'
            : 'Voice note applied',
        { duration: 3000 }
      );
    },
    [initialEditableFields, getSessionFieldValue]
  );

  const voiceNote = useVoiceNote({
    getCurrentSummary,
    onSummaryUpdated: handleVoiceSummaryUpdated,
    onError: (err, details) => {
      const friendlyMessage =
        details?.status && details.status >= 500
          ? 'The recording finished, but the transcription service is unavailable on the server. You can try again after the service is configured.'
          : err.message || 'Voice note failed. Please try again.';

      logger.error('Voice note transcription failed', {
        status: details?.status,
        url: details?.url,
        error: err,
      });

      setVoiceNoteError(friendlyMessage);

      toast.error(friendlyMessage, { duration: 5000 });
    },
  });

  // Determine loading state (include DiagnosticReport loading)
  const isLoading =
    isLoadingEncounter ||
    isLoadingPatient ||
    isLoadingEncounterDiagnosticReport ||
    isLoadingFhirSummary;

  // Determine error state
  const hasError = isErrorEncounter || isErrorPatient || !encounter || !patient;
  const errorMessage = hasError ? 'Failed to load case sheet' : null;

  // Get practitioner name from logged-in user (portal is only used by doctors)
  const practitionerName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username
    : 'Unknown';

  // Prepare data for the view component
  const patientName = getPatientNameFromFhir(patient?.name) || 'Unknown Patient';

  const headerData = {
    patientName,
    consultationDate: encounter?.period?.start
      ? new Date(encounter.period.start).toLocaleDateString()
      : new Date().toLocaleDateString(),
    consultationTime: encounter?.period?.start
      ? new Date(encounter.period.start).toLocaleTimeString()
      : new Date().toLocaleTimeString(),
    status: encounter?.status || 'Unknown',
    doctorName: practitionerName,
  };

  const patientViewData = {
    name: patientName,
    age: patient?.birthDate
      ? new Date().getFullYear() - new Date(patient.birthDate).getFullYear()
      : 0,
    gender: patient?.gender?.toUpperCase() || 'U',
    dateOfBirth: patient?.birthDate || new Date().toISOString(),
    phoneNumber: patient?.telecom?.find((t) => t.system === 'phone')?.value,
    identifier: patient?.identifier?.[0]?.value || patient?.id || 'Unknown',
  };

  // Resolve reference slokas from the session summary (KB-enriched Ayurveda references).
  // Keep the complete set shown during consultation. Some KB hits have only
  // Sanskrit OR meaning/title after relaxed filtering, so requiring both here
  // made Case Sheet show fewer slokas than the Consultation page.
  const resolvedReferenceSlokas = (
    sessionSummary?.suggestive_slokas ??
    (latestReportJson?.reference_slokas as ReferenceSlokaItem[] | undefined) ??
    []
  ).filter(
    (s: ReferenceSlokaItem) =>
      s &&
      ((typeof s.sanskrit === 'string' && s.sanskrit.trim()) ||
        (typeof s.meaning === 'string' && s.meaning.trim()) ||
        (typeof s.title === 'string' && s.title.trim()))
  );

  const consultationViewData = {
    aiAnalysis: resolvedAIAnalysis,
    status:
      linkedAppointment?.status === 'fulfilled' ? ('completed' as const) : ('in-progress' as const),
    createdAt: encounter?.period?.start || new Date().toISOString(),
    completedAt: encounter?.period?.end,
    consultationId: consultationId,
    prescribedMedications: fieldsForView.prescribedMedications,
    cdssSuggestions: cdssSuggestions,
    referenceSlokas: resolvedReferenceSlokas.length > 0 ? resolvedReferenceSlokas : undefined,
    vitals: encounterVitals,
    duration,
    modeOfOnset,
    pastIllness,
    treatmentHistory,
    historyFamilyHistory,
    currentMedication,
    familySocialOccupational,
    personalHistory,
    diagnosisAyurvedic,
    diagnosisAllopathic,
    treatmentMedicines,
    treatmentMedicinesTable,
    treatmentPrinciple,
    treatmentGiven,
  };

  // Show warning if no analysis data
  if (!isLoading && !aiAnalysis && !sessionSummary) {
    logger.warn('No aiAnalysis or sessionSummary data available for case sheet');
  }

  const sendToInvestigationMutation = useMutation({
    mutationFn: () => {
      const investigations = fieldsForView.investigations
        .split(/\r?\n|,/)
        .map((item: string) => item.trim())
        .filter(Boolean);

      if (investigations.length === 0) {
        throw new Error('Add at least one investigation before sending.');
      }
      if (!user?.id || !patientId || !effectiveEncounterId) {
        throw new Error('Missing patient, practitioner, or encounter information.');
      }

      const practitionerName =
        user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.username;

      return investigationOrdersApi.create({
        patientId,
        patientName,
        practitionerId: user.id,
        practitionerName,
        encounterId: effectiveEncounterId,
        investigations,
      });
    },
    onSuccess: (result) => {
      toast.success(
        result.alreadyExists
          ? 'Already sent to Investigations for this consultation'
          : 'Sent to Investigations'
      );
      // The Investigation Orders page (if open elsewhere) has its own cache —
      // invalidate it so it picks up this new/updated order without needing
      // a manual refresh there.
      queryClient.invalidateQueries({ queryKey: ['investigation-orders'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to send to investigations');
    },
  });

  const actions = {
    onBack: undefined, // No back navigation allowed from case sheet
    onPrint: handlePrint,
    onExport: handleExport,
    onCopy: handleCopy,
    // Always provide save handler - handleSave will handle edge cases internally
    onSave: handleSave,
    isSaving: saveMutation.isPending,
    isSaved: isSaved || linkedAppointment?.status === 'fulfilled',
    onSaveDraft: handleSaveDraft,
    isDraftSaving: draftSaveMutation.isPending,
    autosaveStatus: autosave.status,
    autosaveAt: autosave.at,
    onRetryAutosave: () => {
      const fields = editableFields ?? initialEditableFields;
      if (fields) persistToDb(fields).catch((e) => logger.error('Autosave retry failed:', e));
    },
    onSendToInvestigation: () => sendToInvestigationMutation.mutate(),
    isSendingToInvestigation: sendToInvestigationMutation.isPending,
  };

  const dietItems = firstNonEmptyArray(
    sessionSummary?.diet_recommendations,
    sessionSummary?.diet_modifications,
    latestReportJson?.diet_recommendations,
    latestReportJson?.diet_modifications,
    latestReportJson?.commonly_eaten_food
  );

  const lifestyleItems = firstNonEmptyArray(
    sessionSummary?.lifestyle_recommendations,
    sessionSummary?.lifestyle_modifications,
    latestReportJson?.lifestyle_recommendations,
    latestReportJson?.lifestyle_modifications,
    latestReportJson?.lifestyle
  );

  const viruddhaAhaaraItems = firstNonEmptyArray(
    sessionSummary?.viruddha_ahaara,
    sessionSummary?.viruddha_ahara_detected,
    latestReportJson?.viruddha_ahaara,
    latestReportJson?.viruddha_ahara_detected
  );

  // Fallback: when structured arrays are empty, extract from all available text.
  // The Ayurveda summarizer often puts diet/lifestyle advice as a combined block in
  // treatment_plan_management or lifestyle_modifications rather than separate arrays.
  // Apply getSessionFieldValue to handle both { value: "..." } objects and plain strings.
  const _tpText =
    getSessionFieldValue(sessionSummary?.treatment_plan_management) ||
    getSessionFieldValue(latestReportJson?.treatment_plan_management) ||
    '';
  const _lifestyleText =
    getSessionFieldValue(sessionSummary?.lifestyle_recommendations) ||
    getSessionFieldValue(sessionSummary?.lifestyle_modifications) ||
    getSessionFieldValue(latestReportJson?.lifestyle_recommendations) ||
    '';
  const _patientSummaryText =
    getSessionFieldValue(sessionSummary?.patient_summary) ||
    getSessionFieldValue(latestReportJson?.patient_summary) ||
    '';
  // Combine all available text so patterns can be matched across any source
  const allRecommendationsText = [_tpText, _lifestyleText, _patientSummaryText]
    .filter(Boolean)
    .join('\n');

  const effectiveDietItems =
    dietItems.length > 0
      ? dietItems
      : extractClinicalItems(allRecommendationsText, [
          /avoid.*food|avoid.*drink|diet|pathya|apathya|no spic|no milk|no tea|no coffee|no citrus|deep.fried|ferment|sour food|spicy|salty food|fried food|dairy/i,
        ]);

  // Lifestyle's fallback deliberately excludes patient_summary text (unlike
  // Diet/Viruddha above). The AI's patient_summary narrative routinely
  // restates Personal History content in prose (e.g. "The patient exercises
  // 5 times a week..."), and this fallback's keyword list (sleep/exercise/
  // morning/routine) directly overlaps with Personal History concepts — so
  // scanning patient_summary here duplicated already-correct Personal
  // History data into Lifestyle as a false positive. treatment_plan/
  // lifestyle_modifications text is a genuine doctor-recommendation source
  // and stays.
  const lifestyleFallbackText = [_tpText, _lifestyleText].filter(Boolean).join('\n');
  const effectiveLifestyleItems =
    lifestyleItems.length > 0
      ? lifestyleItems
      : extractClinicalItems(lifestyleFallbackText, [
          /sleep|mobile|phone|sun|exercise|abhyanga|oil|lifestyle|stress|routine|wake|morning/i,
        ]);

  const effectiveViruddhaItems =
    viruddhaAhaaraItems.length > 0
      ? viruddhaAhaaraItems
      : extractClinicalItems(allRecommendationsText, [
          /viruddha|incompatible|fish.*milk|milk.*fish|honey.*hot|hot.*honey/i,
        ]);

  return (
    <AppLayout showHeader={false}>
      <CaseSheetView
        header={headerData}
        patient={patientViewData}
        consultation={consultationViewData}
        actions={actions}
        voiceNote={{
          state: voiceNote.state,
          recordingDuration: voiceNote.recordingDuration,
          onStart: voiceNote.startRecording,
          onStop: voiceNote.stopRecording,
          errorMessage: voiceNoteError,
        }}
        loading={isLoading}
        error={errorMessage}
        fields={fieldsForView}
        onFieldChange={handleFieldChange}
        onVitalsChange={patientId && effectiveEncounterId ? saveVitalsMutation.mutate : undefined}
        isSavingVitals={saveVitalsMutation.isPending}
        onAddMedication={handleAddMedication}
        onReplaceMedication={handleReplaceMedication}
        patientId={patientId}
        diet={isAyurveda ? effectiveDietItems : undefined}
        lifestyle={isAyurveda ? effectiveLifestyleItems : undefined}
        viruddhaAhaara={isAyurveda ? effectiveViruddhaItems : undefined}
        isAyurveda={isAyurveda}
      />
    </AppLayout>
  );
}
